import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, set, update, push, get, onDisconnect, runTransaction } from 'firebase/database';

import {database} from '../../database/firebase';
import { sendReport } from '../functions/sendReport';
import { isMultiplaySessionEnabled } from '../../utils/sessionFlags';
import './ludoGamePopup.css';

const isComputerPlayLimitReached = (data) => {
  if (isMultiplaySessionEnabled()) return false;
  return !!(
    data?.success &&
    (data.limitReached === true || Number(data.completedCount) >= 3)
  );
};

const setLudoGameCompletedIfSinglePlay = (payload) => {
  if (isMultiplaySessionEnabled()) return;
  try {
    localStorage.setItem('ludoGameCompleted', JSON.stringify(payload));
  } catch (e) {
    // ignore
  }
};

// Firebase Configuration - REPLACE WITH YOUR OWN CONFIG
// const firebaseConfig = {
//   apiKey: "AIzaSyClcBReah7t7epOyRRz6DWCvaqdjl4baKs",
//   authDomain: "ludo-94666.firebaseapp.com",
//   databaseURL: "https://ludo-94666-default-rtdb.firebaseio.com",
//   projectId: "ludo-94666",
//   storageBucket: "ludo-94666.firebasestorage.app",
//   messagingSenderId: "555536231578",
//   appId: "1:555536231578:web:25aa71a4437bdf1d268323",
//   measurementId: "G-8741F96YS2"
// };

// // Initialize Firebase
// let app, database;
// try {
//   app = initializeApp(firebaseConfig);
//   database = getDatabase(app);
// } catch (error) {
//   console.error("Firebase initialization error:", error);
// }

const COLORS = ['green', 'yellow', 'blue', 'red'];
const COLOR_HEX = { green: '#51cf66', yellow: '#ffd43b', blue: '#4dabf7', red: '#ff6b6b' };
/** Pre-start lobby: joined-player profile cards (shared chrome) */
const LOBBY_JOINED_CARD_GRADIENT =
  'linear-gradient(180deg, #818cf8 0%, #a5b4fc 38%, #e0f2fe 100%)';
const LOBBY_JOINED_CARD_BORDER = '#7dd3fc';
const LOBBY_JOINED_BADGE_BG = '#1e3a8a';
const LOBBY_JOINED_BADGE_BORDER = '#0f766e';
const LOBBY_JOINED_CARD_GAP_PX = 16;
const LOBBY_JOINED_CARD_WIDTH_PX = 172;
/** Fallback when seat color is missing from theme map */
const LOBBY_CARD_DEFAULT_THEME = {
  gradient: LOBBY_JOINED_CARD_GRADIENT,
  border: LOBBY_JOINED_CARD_BORDER,
  badgeBg: LOBBY_JOINED_BADGE_BG,
  badgeBorder: LOBBY_JOINED_BADGE_BORDER,
  avatarBg: '#3B82F6',
};
const LOBBY_CARD_THEME = {
  green: {
    gradient:
      'linear-gradient(180deg, #86efac 0%, #bbf7d0 38%, #ecfdf5 100%)',
    border: '#22c55e',
    badgeBg: '#14532d',
    badgeBorder: '#166534',
    avatarBg: COLOR_HEX.green,
  },
  yellow: {
    gradient:
      'linear-gradient(180deg, #fde047 0%, #fef08a 38%, #fefce8 100%)',
    border: '#ca8a04',
    badgeBg: '#713f12',
    badgeBorder: '#854d0e',
    avatarBg: COLOR_HEX.yellow,
  },
  blue: {
    gradient:
      'linear-gradient(180deg, #93c5fd 0%, #bfdbfe 38%, #eff6ff 100%)',
    border: '#2563eb',
    badgeBg: '#1e3a8a',
    badgeBorder: '#1d4ed8',
    avatarBg: COLOR_HEX.blue,
  },
  red: {
    gradient:
      'linear-gradient(180deg, #fca5a5 0%, #fecaca 38%, #fef2f2 100%)',
    border: '#ef4444',
    badgeBg: '#7f1d1d',
    badgeBorder: '#991b1b',
    avatarBg: COLOR_HEX.red,
  },
};
const DICE_FACES = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];

// Notify live mode backend when game is initiated
const notifyLiveGameStart = async (gameId, players) => {
  try {
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
    const userData = stored ? JSON.parse(stored) : {};
    const sessionId = userData.sessionId || null;

    const playersList = players
      ? Object.values(players).map(p => ({
          userId: p.userId || null,
          name: p.name || 'Unknown',
        }))
      : [];

    const body = {
      sessionId,
      gameId,
      status: 'initiated',
      players: playersList,
      phoneNumber: userData.phoneNumber || null,
    };

    await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/save-computer-multiplayer.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.error('Failed to notify live game start API', err);
  }
};

// Notify live mode backend when game ends
const notifyLiveGameEnd = async (gameId, winnerId, outcome) => {
  try {
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
    const userData = stored ? JSON.parse(stored) : {};
    const sessionId = userData.sessionId || null;

    const body = {
      sessionId,
      gameId,
      status: 'completed',
      outcome,
      winnerId,
    };

    const res = await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/save-computer-multiplayer.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = res.ok ? await res.json().catch(() => ({})) : {};
    if (data.send_report === true) {
      const reportData = {
        sessionId: userData.sessionId,
        role: userData.source,
        token: userData.token || null,
        name: userData.name,
        userId: userData.userId,
        gameId: userData.gameId || '',
        organizationId: userData.organizationId,
        points: (winnerId && userData.userId && String(winnerId) === String(userData.userId)) ? 10 : 0,
        time: data.duration || '00:00:00',
        reportId: userData.reportId || null,
        ans: '',
      };
      sendReport(reportData)
        .then((apiRes) => console.log('Report API SUCCESS:', apiRes))
        .catch((err) => console.error('Report API FAILED:', err));
    }
  } catch (err) {
    console.error('Failed to notify live game end API', err);
  }
};

// Mark tournament player as present in MySQL (match_players.isPresent = 1)
// Only used for deep-linked tournament games (where tournamentId is available in URL).
const markTournamentPlayerPresentApi = async (gameId, userId) => {
  if (!gameId || !userId) return;
  try {
    await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/setPlayerPresent.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gameId, userId }),
    });
  } catch (err) {
    console.error('Failed to mark player present:', err);
  }
};

// Notify backend API when a winner is decided
// urlTournamentId: optional tournamentId from URL (e.g. from /ludo-game?tournamentId=...), used for redirect when API does not return it
const notifyWinnerApi = async (gameId, color, mode, outcome = 'win', urlTournamentId = null) => {
  if (!gameId || !color) return null;

  try {
    const body = { gameId, color, mode, outcome };
    if (mode === 'live' && typeof sessionStorage !== 'undefined') {
      const stored = sessionStorage.getItem('userData');
      if (stored) {
        try {
          const userData = JSON.parse(stored);
          body.userData = userData;
        } catch (e) {
          // ignore parse error
        }
      }
    }

    const res = await fetch(process.env.REACT_APP_FOLDER_LINK+'/api/save-winner.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    let data;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    const success =
      (data && data.response && data.response.success) ?? data?.success;
    const tournamentId =
      (data && data.response && data.response.tournamentId) ??
      data?.tournamentId ??
      urlTournamentId;

    return { success, tournamentId };
  } catch (err) {
    console.error('Failed to notify winner API', err);
    return null;
  }
};

const getMinPlayersToStart = (gameType, { tournament = false } = {}) => {
  // Tournament: always start as soon as 2 players join; late joiners can enter after start.
  if (tournament) return 2;
  const n = parseInt(gameType, 10);
  return [2, 3, 4].includes(n) ? n : 2;
};

// Parse URL query parameters for deep-linked Ludo games
const parseLudoConfigFromUrl = () => {
  if (typeof window === 'undefined') return {};

  try {
    const params = new URLSearchParams(window.location.search || '');

    const rawGameId = params.get('gameId') || params.get('game_id');
    const rawMode = params.get('mode');
    const rawName = params.get('name');
    const rawUserId = params.get('userId') || params.get('user_id');
    const rawColor = params.get('color');
    const rawTournamentId = params.get('tournamentId') || params.get('tournament_id');
    const rawGameType = params.get('gameType') || params.get('game_type');
    const parsedGameType = parseInt(rawGameType, 10);
    const gameType = [2, 3, 4].includes(parsedGameType) ? parsedGameType : 2;

    const normalizedColor = rawColor ? rawColor.toLowerCase() : null;
    const color = COLORS.includes(normalizedColor) ? normalizedColor : null;

    // Only treat recognised game engines as gameMode; "live" is a routing mode that should
    // still show the landing screen and let user pick multiplayer/computer.
    const mode =
      rawMode === 'multiplayer' || rawMode === 'computer'
        ? rawMode
        : null;

    return {
      gameId: rawGameId || null,
      mode,       // game engine mode ('multiplayer' | 'computer' | null)
      rawMode,    // raw routing mode from URL (can be 'live', etc.)
      name: rawName || null,
      userId: rawUserId || null,
      color,
      tournamentId: rawTournamentId || null,
      gameType,
    };
  } catch (e) {
    console.warn('Failed to parse Ludo URL parameters:', e);
    return {};
  }
};

// === CANONICAL LUDO PATH (GREEN) ===
// This starts from the GREEN arrow square shown in your screenshot
const BASE_PATH = [
  { x: 1, y: 6 },
  { x: 2, y: 6 },
  { x: 3, y: 6 },
  { x: 4, y: 6 },
  { x: 5, y: 6 },
  { x: 6, y: 5 },
  { x: 6, y: 4 },
  { x: 6, y: 3 },
  { x: 6, y: 2 },
  { x: 6, y: 1 },
  { x: 6, y: 0 },
  { x: 7, y: 0 },
  { x: 8, y: 0 },
  { x: 8, y: 1 },
  { x: 8, y: 2 },
  { x: 8, y: 3 },
  { x: 8, y: 4 },
  { x: 8, y: 5 },
  { x: 9, y: 6 },
  { x: 10, y: 6 },
  { x: 11, y: 6 },
  { x: 12, y: 6 },
  { x: 13, y: 6 },
  { x: 14, y: 6 },
  { x: 14, y: 7 },
  { x: 14, y: 8 },
  { x: 13, y: 8 },
  { x: 12, y: 8 },
  { x: 11, y: 8 },
  { x: 10, y: 8 },
  { x: 9, y: 8 },
  { x: 8, y: 9 },
  { x: 8, y: 10 },
  { x: 8, y: 11 },
  { x: 8, y: 12 },
  { x: 8, y: 13 },
  { x: 8, y: 14 },
  { x: 7, y: 14 },
  { x: 6, y: 14 },
  { x: 6, y: 13 },
  { x: 6, y: 12 },
  { x: 6, y: 11 },
  { x: 6, y: 10 },
  { x: 6, y: 9 },
  { x: 5, y: 8 },
  { x: 4, y: 8 },
  { x: 3, y: 8 },
  { x: 2, y: 8 },
  { x: 1, y: 8 },
  { x: 0, y: 8 },
  { x: 0, y: 7 },
  { x: 0, y: 6 }
];


const rotatePath = (path, steps) =>
  path.map((_, i) => path[(i + steps) % path.length]);

const PATHS = {
  green: BASE_PATH,
  yellow: rotatePath(BASE_PATH, 13),
  blue: rotatePath(BASE_PATH, 26),
  red: rotatePath(BASE_PATH, 39),
};

const MAIN_PATH_LENGTH = BASE_PATH.length;
const HOME_STRETCH_LENGTH = 5;
// Center is a final step after the 5 home-stretch cells
const CENTER_POSITION = MAIN_PATH_LENGTH + HOME_STRETCH_LENGTH + 1;

// Match piece inline style `transition: left 0.4s ...` so capture writes run after land animation
const PIECE_MOVE_TRANSITION_MS = 400;

const ENTRY_SQUARES = {
  green: { x: 0, y: 7 },
  yellow: { x: 7, y: 0 },
  blue: { x: 14, y: 7 },
  red: { x: 7, y: 14 }
};

const ENTRY_POSITIONS = Object.fromEntries(
  COLORS.map((color) => {
    const entry = ENTRY_SQUARES[color];
    const idx = PATHS[color].findIndex((cell) => cell.x === entry.x && cell.y === entry.y);
    return [color, idx >= 0 ? idx + 1 : MAIN_PATH_LENGTH];
  })
);

const HOME_STRETCHES = {
  green: [
    { x: 1, y: 7 },
    { x: 2, y: 7 },
    { x: 3, y: 7 },
    { x: 4, y: 7 },
    { x: 5, y: 7 }
  ],
  yellow: [
    { x: 7, y: 1 },
    { x: 7, y: 2 },
    { x: 7, y: 3 },
    { x: 7, y: 4 },
    { x: 7, y: 5 }
  ],
  blue: [
    { x: 13, y: 7 },
    { x: 12, y: 7 },
    { x: 11, y: 7 },
    { x: 10, y: 7 },
    { x: 9, y: 7 }
  ],
  red: [
    { x: 7, y: 13 },
    { x: 7, y: 12 },
    { x: 7, y: 11 },
    { x: 7, y: 10 },
    { x: 7, y: 9 }
  ]
};

const isValidPosition = (color, position) => {
  if (!Number.isInteger(position)) return false;
  if (position === 0 || position === CENTER_POSITION) return true;
  if (position >= 1 && position <= MAIN_PATH_LENGTH) {
    return Boolean(PATHS[color]?.[position - 1]);
  }
  if (position >= MAIN_PATH_LENGTH + 1 && position < CENTER_POSITION) {
    const idx = position - (MAIN_PATH_LENGTH + 1);
    return Boolean(HOME_STRETCHES[color]?.[idx]);
  }
  return false;
};


const LudoGame = ({ updateHeader }) => {
  const urlConfig = parseLudoConfigFromUrl();
  const isLiveMode = urlConfig.rawMode === 'live';

  const [gameId, setGameId] = useState(
    urlConfig.gameId || (isLiveMode ? null : localStorage.getItem('ludoGameId')) || null
  );
  const [playerName, setPlayerName] = useState(
    urlConfig.name || localStorage.getItem('ludoPlayerName') || ''
  );
  const [playerColor, setPlayerColor] = useState(() => {
    // In live mode we deliberately ignore URL color so the user can pick
    if (!isLiveMode && urlConfig.color && COLORS.includes(urlConfig.color)) {
      return urlConfig.color;
    }
    const storedColor = localStorage.getItem('ludoPlayerColor');
    return COLORS.includes(storedColor) ? storedColor : null;
  });
  const [gameState, setGameState] = useState(null);
  const [showJoinCreate, setShowJoinCreate] = useState(
    !(urlConfig.gameId || (isLiveMode ? null : localStorage.getItem('ludoGameId')))
  );
  const [joinGameId, setJoinGameId] = useState('');
  const [selectedColor, setSelectedColor] = useState(null);
  const [takenColors, setTakenColors] = useState([]);
  const [checkingExistingGame, setCheckingExistingGame] = useState(isLiveMode && !urlConfig.gameId);
  const [movablePieces, setMovablePieces] = useState([]);
  const [diceDisplayValue, setDiceDisplayValue] = useState(null);
  const [isDiceRolling, setIsDiceRolling] = useState(false);
  const [boardSize, setBoardSize] = useState(560);
  const boardSizeRef = useRef(560);
  const [turnTimer, setTurnTimer] = useState(30);
  const timerRef = useRef(null);
  const [gameMode, setGameMode] = useState(
    urlConfig.mode === 'computer' || urlConfig.mode === 'multiplayer'
      ? urlConfig.mode
      : isLiveMode ? 'computer' : 'multiplayer'
  ); // 'multiplayer' or 'computer'
  const [aiOpponentCount, setAiOpponentCount] = useState(isLiveMode ? 1 : 3); // 1, 2, or 3
  const [liveThemeFlags, setLiveThemeFlags] = useState({
    computer: false,
    multiplayer: false,
    computer_v3: false,
    computer_v4: false
  });
  const aiTurnTimeoutRef = useRef(null);
  const [isMoving, setIsMoving] = useState(false);
  const prevTurnRef = useRef(null);
  const winnerNotifiedRef = useRef(false);
  const [showGameStartedIndicator, setShowGameStartedIndicator] = useState(false);
  const [showYourTurnIndicator, setShowYourTurnIndicator] = useState(false);
  const startedShownRef = useRef(false);
  const abandonmentCheckRef = useRef(null);
  const abandonmentNotifiedRef = useRef(false);
  const tournamentAutoStartAttemptedRef = useRef(false);
  const [lobbyTimerSeconds, setLobbyTimerSeconds] = useState(600);
  const lobbyTimerIntervalRef = useRef(null);
  const lastActivityRef = useRef(null);
  const lastActivePlayerRef = useRef(null);
  const prevOnlineStatusRef = useRef({});
  const presenceCleanupRef = useRef(null);
  // Dedupe MySQL "present" writes: only once per {gameId}:{userId}
  const presentMarkedRef = useRef({});
  const navigate = useNavigate();

  const [gamePopup, setGamePopup] = useState(null);
  const toastTimerRef = useRef(null);
  const alertResolveRef = useRef(null);
  const confirmResolveRef = useRef(null);

  const closeGamePopup = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setGamePopup(null);
  }, []);

  const finishGameAlert = useCallback(() => {
    const r = alertResolveRef.current;
    alertResolveRef.current = null;
    setGamePopup(null);
    if (r) r();
  }, []);

  const finishGameConfirm = useCallback((confirmed) => {
    const r = confirmResolveRef.current;
    confirmResolveRef.current = null;
    setGamePopup(null);
    if (r) r(confirmed);
  }, []);

  const showGameAlert = useCallback(
    ({
      variant,
      title,
      message,
      allowBackdropClose = true,
      okText = 'OK',
      alertDecoration,
    }) =>
      new Promise((resolve) => {
        alertResolveRef.current = resolve;
        setGamePopup({
          kind: 'alert',
          variant,
          title,
          message,
          allowBackdropClose,
          okText,
          alertDecoration,
        });
      }),
    []
  );

  const showGameToast = useCallback(({ variant, title, duration = 3000 }) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setGamePopup({ kind: 'toast', variant, title });
    toastTimerRef.current = setTimeout(() => {
      setGamePopup(null);
      toastTimerRef.current = null;
    }, duration);
  }, []);

  const showGameConfirm = useCallback(
    ({ title, message, confirmText, cancelText }) =>
      new Promise((resolve) => {
        confirmResolveRef.current = resolve;
        setGamePopup({
          kind: 'confirm',
          variant: 'warning',
          title,
          message,
          confirmText,
          cancelText,
        });
      }),
    []
  );

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  const fetchComputerExistStatus = async () => {
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
    if (!stored) return null;
    const uData = JSON.parse(stored);
    const res = await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/check-computer-exist.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: uData.sessionId,
        userId: urlConfig.userId || uData.userId
      })
    });
    if (!res.ok) return null;
    return await res.json();
  };

  const resetForNextComputerGame = () => {
    localStorage.removeItem('ludoGameId');
    localStorage.removeItem('ludoPlayerColor');
    localStorage.removeItem('ludoPlayerName');
    winnerNotifiedRef.current = false;
    abandonmentNotifiedRef.current = false;
    prevOnlineStatusRef.current = {};
    setGameId(null);
    setPlayerColor(null);
    setSelectedColor(null);
    setShowJoinCreate(true);
    setGameState(null);
  };

  const goToThankYouAfterLiveGame = (liveGameMode = 'computer') => {
    try {
      sessionStorage.setItem('ludoLastLiveGameMode', liveGameMode);
    } catch (e) {
      // ignore
    }
    resetForNextComputerGame();
    navigate('/thankyou');
  };

  const handleLiveMultiplayerCompletionFlow = (tournamentId = null) => {
    if (tournamentId) {
      window.location.href = `/tournament-structure/${tournamentId}`;
      return;
    }
    goToThankYouAfterLiveGame('multiplayer');
  };

  const handleLiveComputerCompletionFlow = async () => {
    try {
      const data = await fetchComputerExistStatus();
      if (isComputerPlayLimitReached(data)) {
        setLudoGameCompletedIfSinglePlay({
          allCompleted: true,
          completedCount: Number(data.completedCount) || 3,
          limitReached: true,
        });
        navigate('/thankyou');
        return;
      }
      if (isMultiplaySessionEnabled()) {
        goToThankYouAfterLiveGame('computer');
        return;
      }
      resetForNextComputerGame();
    } catch (err) {
      console.error('Error handling live computer completion flow:', err);
      if (isMultiplaySessionEnabled()) {
        goToThankYouAfterLiveGame('computer');
      } else {
        resetForNextComputerGame();
      }
    }
  };

  useEffect(() => {
    if (updateHeader) updateHeader({ backButtonRedirect: "/rules" });
  }, [updateHeader]);

  // Live mode only: read theme flags from sessionStorage (same source as rules.jsx)
  useEffect(() => {
    if (!isLiveMode) return;
    const themeRaw = sessionStorage.getItem('themeData');
    if (!themeRaw) return;
    try {
      const theme = JSON.parse(themeRaw);
      const flags = {
        computer: !!theme.computer,
        multiplayer: !!theme.multiplayer,
        computer_v3: !!theme.computer_v3,
        computer_v4: !!theme.computer_v4
      };
      setLiveThemeFlags(flags);
      setGameMode((prev) => {
        const onlyComputer = flags.computer && !flags.multiplayer;
        const onlyMultiplayer = flags.multiplayer && !flags.computer;
        if (onlyComputer) return 'computer';
        if (onlyMultiplayer) return 'multiplayer';
        if (!flags.computer && !flags.multiplayer) return prev;
        return urlConfig.mode === 'computer' || urlConfig.mode === 'multiplayer' ? urlConfig.mode : (flags.computer ? 'computer' : 'multiplayer');
      });
      setAiOpponentCount((prev) => {
        const allowed = [1, ...(flags.computer_v3 ? [2] : []), ...(flags.computer_v4 ? [3] : [])];
        return allowed.includes(prev) ? prev : (allowed[0] ?? 1);
      });
    } catch (e) {
      setLiveThemeFlags({ computer: false, multiplayer: false, computer_v3: false, computer_v4: false });
    }
  }, [isLiveMode]);

  // After thank-you "Play again", restore last live mode (multiplayer vs computer)
  useEffect(() => {
    if (!isLiveMode) return;
    try {
      const last = sessionStorage.getItem('ludoLastLiveGameMode');
      if (last === 'multiplayer' || last === 'computer') {
        setGameMode(last);
      }
    } catch (e) {
      // ignore
    }
  }, [isLiveMode]);

  // Check for existing game in live mode on component mount
  useEffect(() => {
    if (!isLiveMode || urlConfig.gameId) {
      setCheckingExistingGame(false);
      return;
    }

    const checkExistingGame = async () => {
      try {
        const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
        if (!stored) {
          setCheckingExistingGame(false);
          setShowJoinCreate(true);
          return;
        }

        const data = await fetchComputerExistStatus();
        if (!data) {
          setShowJoinCreate(true);
          return;
        }

        if (isComputerPlayLimitReached(data)) {
          setLudoGameCompletedIfSinglePlay({
            allCompleted: true,
            completedCount: Number(data.completedCount) || 3,
            limitReached: true,
          });
          setCheckingExistingGame(false);
          navigate('/thankyou');
          return;
        }

        if (data.success && data.exists && data.gameId) {
          setGameId(data.gameId);
          setShowJoinCreate(false);
          localStorage.setItem('ludoGameId', data.gameId);
        } else {
          setShowJoinCreate(true);
        }
      } catch (err) {
        console.error('Error checking existing game:', err);
        setShowJoinCreate(true);
      } finally {
        setCheckingExistingGame(false);
      }
    };

    checkExistingGame();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch taken colors when Game ID is entered (for live mode join)
  useEffect(() => {
    if (!isLiveMode || !joinGameId.trim() || !database) {
      setTakenColors([]);
      return;
    }

    const fetchTakenColors = async () => {
      try {
        const gameRef = ref(database, `games/${joinGameId}`);
        const snapshot = await get(gameRef);
        if (snapshot.exists()) {
          const game = snapshot.val();
          const taken = game.players ? Object.keys(game.players) : [];
          setTakenColors(taken);
        } else {
          setTakenColors([]);
        }
      } catch (err) {
        console.error('Error fetching game for color check:', err);
        setTakenColors([]);
      }
    };

    const timeout = setTimeout(fetchTakenColors, 500);
    return () => clearTimeout(timeout);
  }, [joinGameId, isLiveMode]);

  // Show "Game started" indicator for 2 seconds when game starts
  useEffect(() => {
    if (!gameState?.started) {
      startedShownRef.current = false;
      return;
    }
    if (startedShownRef.current) return;
    startedShownRef.current = true;
    setShowGameStartedIndicator(true);
    const t = setTimeout(() => setShowGameStartedIndicator(false), 2000);
    return () => clearTimeout(t);
  }, [gameState?.started]);

  const generateGuestName = () => `Guest_${Math.floor(1000 + Math.random() * 9000)}`;
  
  // Shuffle array helper for randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Play notification sound when it's user's turn
  const playTurnNotification = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Create a pleasant "ding" sound
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // High pitch
      oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Audio notification not supported:', error);
    }
  };

  // AI Logic
  const makeAIMove = async (color) => {
    if (!gameState || gameState.winner || gameState.abandoned || gameState.currentTurn !== color) return;

    const player = gameState.players[color];
    if (!player || !player.isAI) return;

    // Wait before rolling dice to simulate thinking
    await new Promise(resolve => setTimeout(resolve, 750 + Math.random() * 500));

    // Roll dice
    const dice = Math.floor(Math.random() * 6) + 1;
    await update(ref(database, `games/${gameId}`), {
      diceValue: dice,
      lastRoll: Date.now(),
      lastActivity: Date.now(),
      lastActivePlayer: color,
    });

    // Wait for dice to settle and be visible
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Choose piece to move
    const pieces = player.pieces;
    const movablePieces = [];

    pieces.forEach((pos, idx) => {
      if (canMovePiece(pos, dice, color)) {
        movablePieces.push(idx);
      }
    });

    if (movablePieces.length === 0) {
      // No moves available, pass turn
      await nextTurn();
      return;
    }

    // AI strategy: prioritize in order
    // 1. Bring out a new piece if possible (roll 6)
    // 2. Move piece that can capture
    // 3. Move piece closest to home
    // 4. Random piece

    let chosenPiece = movablePieces[0];

    // Strategy 1: Bring out new piece if roll is 6
    if (dice === 6) {
      const homepiece = movablePieces.find(idx => pieces[idx] === 0);
      if (homepiece !== undefined) {
        chosenPiece = homepiece;
      }
    }

    // Strategy 2: Check for capture opportunity
    if (chosenPiece === movablePieces[0]) {
      for (const pieceIdx of movablePieces) {
        const currentPos = pieces[pieceIdx];
        let newPos;

        if (currentPos === 0) {
          newPos = 1;
        } else if (currentPos >= 1 && currentPos <= MAIN_PATH_LENGTH) {
          const entryPos = ENTRY_POSITIONS[color] || MAIN_PATH_LENGTH;
          const target = currentPos + dice;

        if (currentPos <= entryPos && target > entryPos) {
          const stepsIntoHome = target - entryPos;
          newPos = MAIN_PATH_LENGTH + stepsIntoHome;
        } else {
          newPos = Math.min(target, MAIN_PATH_LENGTH);
        }
        } else {
          newPos = Math.min(currentPos + dice, CENTER_POSITION);
        }
        
        if (newPos >= 1 && newPos <= MAIN_PATH_LENGTH) {
          const myCell = PATHS[color][newPos - 1];
          if (myCell) {
            const myKey = `${myCell.x},${myCell.y}`;
            const safeCells = new Set([
              "1,6","8,1","13,8","6,13",
              "6,2","2,8","8,12","12,6"
            ]);

            if (!safeCells.has(myKey)) {
              // Check if any opponent is at this position
              for (const enemyColor of Object.keys(gameState.players)) {
                if (enemyColor === color) continue;
                const enemyPieces = gameState.players[enemyColor].pieces;
                for (let i = 0; i < enemyPieces.length; i++) {
                  const enemyPos = enemyPieces[i];
                  if (enemyPos >= 1 && enemyPos <= MAIN_PATH_LENGTH) {
                    const enemyCell = PATHS[enemyColor][enemyPos - 1];
                    if (enemyCell && `${enemyCell.x},${enemyCell.y}` === myKey) {
                      chosenPiece = pieceIdx;
                      break;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // Strategy 3: Move piece furthest along
    if (chosenPiece === movablePieces[0]) {
      let maxPos = -1;
      for (const pieceIdx of movablePieces) {
        if (pieces[pieceIdx] > maxPos) {
          maxPos = pieces[pieceIdx];
          chosenPiece = pieceIdx;
        }
      }
    }

    // Execute move using same logic as human player
    const currentPieces = [...pieces];
    const currentPos = currentPieces[chosenPiece];
    let newPos;

    if (currentPos === 0) {
      newPos = 1;
    } else if (currentPos >= 1 && currentPos <= MAIN_PATH_LENGTH) {
      const entryPos = ENTRY_POSITIONS[color] || MAIN_PATH_LENGTH;
      const target = currentPos + dice;

      if (currentPos <= entryPos && target > entryPos) {
        // Crossing into home stretch after reaching entry square
        const stepsIntoHome = target - entryPos;
        newPos = MAIN_PATH_LENGTH + stepsIntoHome;
      } else {
        // Normal move on main path (including landing on entry square)
        newPos = Math.min(target, MAIN_PATH_LENGTH);
      }
    } else if (currentPos > MAIN_PATH_LENGTH && currentPos <= CENTER_POSITION) {
      // Already in home stretch
      newPos = Math.min(currentPos + dice, CENTER_POSITION);
    } else {
      await nextTurn();
      return;
    }

    if (!isValidPosition(color, newPos)) {
      console.warn(`[AI MOVE] Illegal position for ${color}: ${newPos}`);
      await nextTurn();
      return;
    }

    currentPieces[chosenPiece] = newPos;

    // Check for captures and track if AI captured (extra turn)
    let didCapture = false;
    const captureEnemyUpdates = [];
    if (newPos >= 1 && newPos <= MAIN_PATH_LENGTH) {
      const safeCells = new Set([
        "1,6","8,1","13,8","6,13",
        "6,2","2,8","8,12","12,6"
      ]);

      const playerPath = PATHS[color];
      if (playerPath) {
        const myCell = playerPath[newPos - 1];
        if (myCell) {
          const myKey = `${myCell.x},${myCell.y}`;

          if (!safeCells.has(myKey)) {
            for (const enemyColor of Object.keys(gameState.players)) {
              if (enemyColor === color) continue;

              const prevPieces = gameState.players[enemyColor].pieces;
              const enemyPieces = prevPieces.map((pos) => {
                if (typeof pos !== 'number' || pos < 1 || pos > MAIN_PATH_LENGTH) {
                  return pos;
                }

                const enemyPath = PATHS[enemyColor];
                if (!enemyPath) return pos;

                const enemyCell = enemyPath[pos - 1];
                if (!enemyCell) return pos;

                const enemyKey = `${enemyCell.x},${enemyCell.y}`;

                if (enemyKey === myKey) {
                  didCapture = true;
                  return 0;
                }
                return pos;
              });

              if (enemyPieces.some((p, i) => p !== prevPieces[i])) {
                captureEnemyUpdates.push({ color: enemyColor, pieces: enemyPieces });
              }
            }
          }
        }
      }
    }

    const reachedCenter = newPos === CENTER_POSITION;
    const finished = currentPieces.filter(p => p === CENTER_POSITION).length;

    await update(ref(database, `games/${gameId}/players/${color}`), {
      pieces: currentPieces,
      finished: finished
    });

    if (didCapture && captureEnemyUpdates.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, PIECE_MOVE_TRANSITION_MS));
      for (const { color: enemyColor, pieces } of captureEnemyUpdates) {
        await update(ref(database, `games/${gameId}/players/${enemyColor}`), {
          pieces
        });
      }
    }

    if (finished === 4) {
      await update(ref(database, `games/${gameId}`), {
        winner: color
      });
      // Inform tournament backend about the winner
      notifyWinnerApi(gameId, color, gameState?.gameMode || 'multiplayer', 'win', urlConfig.tournamentId);
      if (isLiveMode) {
        const winnerId = gameState?.players?.[color]?.userId || null;
        notifyLiveGameEnd(gameId, winnerId, 'win');
      }
      return;
    }

    // Wait after move to show the result
    await new Promise(resolve => setTimeout(resolve, gameState?.gameMode === 'computer' ? 600 : 1200));

    // Extra turn: on 6, or when piece reaches center, or when capturing an opponent
    const extraTurn = dice === 6 || reachedCenter || didCapture;
    if (!extraTurn) {
      await nextTurn();
    } else {
      await update(ref(database, `games/${gameId}`), {
        diceValue: null,
        lastRoll: null,
        turnStartTime: Date.now()
      });
    }
  };

  const sanitizePieces = (color, pieces) =>
    pieces.map((pos) => (isValidPosition(color, pos) ? pos : 0));

  // Reset one-time refs when switching to a different game
  useEffect(() => {
    winnerNotifiedRef.current = false;
    abandonmentNotifiedRef.current = false;
    prevOnlineStatusRef.current = {};
  }, [gameId]);

  // Persist identity so reloads without URL params still work
  useEffect(() => {
    if (gameId) {
      localStorage.setItem('ludoGameId', gameId);
    }
    if (playerName) {
      localStorage.setItem('ludoPlayerName', playerName);
    }
    if (playerColor && COLORS.includes(playerColor)) {
      localStorage.setItem('ludoPlayerColor', playerColor);
    }
  }, [gameId, playerName, playerColor]);

  useEffect(() => {
    if (!gameId || !database) return;

    const gameRef = ref(database, `games/${gameId}`);
    let unsubscribe;

    const ensureGameAndSubscribe = async () => {
      try {
        const snapshot = await get(gameRef);

        if (!snapshot.exists()) {
          // Auto-create game if it doesn't exist yet (e.g. deep-linked tournament game)
          const preferredColor =
            (urlConfig.color && COLORS.includes(urlConfig.color) && urlConfig.color) ||
            (playerColor && COLORS.includes(playerColor) && playerColor) ||
            'green';

          const resolvedName =
            (urlConfig.name && urlConfig.name.trim()) ||
            (playerName && playerName.trim()) ||
            'Player';

          const players = {
            [preferredColor]: {
              name: resolvedName,
              pieces: [0, 0, 0, 0],
              finished: 0,
              isAI: false,
              userId: urlConfig.userId || null,
              missedTurns: 0,
              disabled: false,
            },
          };

          const initialState = {
            players,
            currentTurn: preferredColor,
            started: false,
            diceValue: null,
            winner: null,
            lastRoll: null,
            turnStartTime: null,
            gameMode: urlConfig.mode || 'multiplayer',
            colorOrder: null,
          };

          await set(gameRef, initialState);

          // Ensure local color matches created player
          if (!playerColor || playerColor !== preferredColor) {
            setPlayerColor(preferredColor);
          }
        } else {
          // Game already exists — try to rejoin by userId before adding a new player
          const existingData = snapshot.val() || {};
          const existingPlayers = existingData.players || {};

          const currentUserId = urlConfig.userId;
          let reconnected = false;

          // If we have a userId, check if this user already has a seat in the game
          if (currentUserId) {
            const match = Object.entries(existingPlayers).find(
              ([, p]) => p.userId === currentUserId
            );
            if (match) {
              const [matchedColor] = match;
              if (!playerColor || playerColor !== matchedColor) {
                setPlayerColor(matchedColor);
                localStorage.setItem('ludoPlayerColor', matchedColor);
              }
              reconnected = true;
            }
          }

          // Late join allowed while game is in progress; block only after it ended
          const gameEnded = Boolean(existingData.winner || existingData.abandoned);

          // Not a rejoin — add as new player if color/name specified (including after started)
          if (
            !reconnected &&
            !gameEnded &&
            urlConfig.color &&
            COLORS.includes(urlConfig.color) &&
            urlConfig.name
          ) {
            const desiredColor = urlConfig.color;

            if (!existingPlayers[desiredColor]) {
              await update(gameRef, {
                [`players/${desiredColor}`]: {
                  name: urlConfig.name,
                  pieces: [0, 0, 0, 0],
                  finished: 0,
                  isAI: false,
                  userId: currentUserId || null,
                  missedTurns: 0,
                  disabled: false,
                },
              });
            }

            if (!playerColor || playerColor !== desiredColor) {
              setPlayerColor(desiredColor);
            }
          }
        }

        unsubscribe = onValue(gameRef, (snap) => {
          const data = snap.val();
          if (data) {
            // Normalize any illegal piece positions to prevent invalid moves/rendering
            if (data.players) {
              const updates = {};
              Object.entries(data.players).forEach(([color, player]) => {
                if (!player?.pieces || !Array.isArray(player.pieces)) return;
                const sanitized = sanitizePieces(color, player.pieces);
                const changed = sanitized.some((pos, i) => pos !== player.pieces[i]);
                if (changed) {
                  updates[`players/${color}/pieces`] = sanitized;
                }
              });
              if (Object.keys(updates).length > 0) {
                update(gameRef, updates);
              }
            }
            // Normalize missedTurns/disabled for existing games without these fields
            const normalizedData = data.players
              ? {
                  ...data,
                  players: Object.fromEntries(
                    Object.entries(data.players).map(([c, p]) => [
                      c,
                      { ...p, missedTurns: p.missedTurns ?? 0, disabled: p.disabled ?? false },
                    ])
                  ),
                }
              : data;
            setGameState(normalizedData);
            const stateToUse = normalizedData.players ? normalizedData : data;
            if (stateToUse.currentTurn === playerColor && stateToUse.diceValue && stateToUse.lastRoll) {
              checkMovablePieces(stateToUse);
            } else {
              setMovablePieces([]);
            }
          }
        });
      } catch (err) {
        console.error('Error initializing Ludo game for gameId', gameId, err);
      }
    };

    ensureGameAndSubscribe();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [gameId, playerColor, playerName]);

  // Player presence tracking — mark online/offline in Firebase
  useEffect(() => {
    if (!gameId || !playerColor || !database) return;

    const playerOnlineRef = ref(database, `games/${gameId}/players/${playerColor}/online`);
    const playerLastSeenRef = ref(database, `games/${gameId}/players/${playerColor}/lastSeen`);
    const connectedRef = ref(database, '.info/connected');

    const unsubPresence = onValue(connectedRef, (snap) => {
      if (snap.val() === true) {
        set(playerOnlineRef, true);
        set(playerLastSeenRef, Date.now());

        onDisconnect(playerOnlineRef).set(false);
        onDisconnect(playerLastSeenRef).set(Date.now());

        // Tournament-only: mark match_players.isPresent = 1 in MySQL
        // (Do not set back to 0 on disconnect; requirement says "only-true".)
        if (urlConfig.tournamentId) {
          const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
          let parsed = null;
          if (stored) {
            try {
              parsed = JSON.parse(stored);
            } catch (e) {
              parsed = null;
            }
          }

          const resolvedUserId = urlConfig.userId || parsed?.userId || null;
          const key = `${gameId}:${resolvedUserId}`;

          if (resolvedUserId && !presentMarkedRef.current[key]) {
            presentMarkedRef.current[key] = true;
            markTournamentPlayerPresentApi(gameId, resolvedUserId);
          }
        }
      }
    });

    presenceCleanupRef.current = () => {
      set(playerOnlineRef, false);
      set(playerLastSeenRef, Date.now());
    };

    return () => {
      unsubPresence();
      if (presenceCleanupRef.current) {
        presenceCleanupRef.current();
        presenceCleanupRef.current = null;
      }
    };
  }, [gameId, playerColor, urlConfig.tournamentId, urlConfig.userId]);

  // Notify when opponent leaves or rejoins (skip when game already ended)
  useEffect(() => {
    if (!gameState?.players || !playerColor || !gameState?.started) return;
    if (gameState?.winner || gameState?.abandoned) return;

    Object.entries(gameState.players).forEach(([color, player]) => {
      if (color === playerColor || player.isAI) return;

      const wasOnline = prevOnlineStatusRef.current[color];
      const isOnline = player.online;

      if (wasOnline === true && isOnline === false) {
        showGameToast({
          variant: 'warning',
          title: `${player.name || color.toUpperCase()} left the game`,
        });
      } else if (wasOnline === false && isOnline === true) {
        showGameToast({
          variant: 'success',
          title: `${player.name || color.toUpperCase()} rejoined!`,
        });
      }

      prevOnlineStatusRef.current[color] = isOnline;
    });
  }, [gameState?.players, playerColor, gameState?.started, showGameToast]);

  // Whenever we see a winner in Firebase, show popup and notify backend (once per game)
  useEffect(() => {
    if (!gameState?.winner || !gameId || !playerColor) return;
    if (gameState?.abandoned) return;
    if (winnerNotifiedRef.current) return;

    winnerNotifiedRef.current = true;
    const winnerColor = gameState.winner;
    const isCurrentPlayerWinner = winnerColor === playerColor;
    const isLiveComputerMode = isLiveMode && gameState?.gameMode === 'computer';

    const runWinnerFlow = async () => {
      // Dismiss any active toast/popup so winner modal is guaranteed visible
      closeGamePopup();
      await new Promise(resolve => setTimeout(resolve, 300));

      // Notify backend API immediately — don't wait for popup interaction
      let apiResult = null;
      try {
        apiResult = await notifyWinnerApi(
          gameId,
          winnerColor,
          gameState?.gameMode || 'multiplayer',
          'win',
          urlConfig.tournamentId
        );
      } catch (e) {
        console.error('Failed to notify winner API:', e);
      }
      if (isLiveMode) {
        try {
          const winnerId = gameState?.players?.[winnerColor]?.userId || null;
          notifyLiveGameEnd(gameId, winnerId, 'win');
        } catch (e) {
          console.error('Failed to notify live game end:', e);
        }
      }

      if (!isLiveComputerMode) {
        setLudoGameCompletedIfSinglePlay({
          outcome: isCurrentPlayerWinner ? 'win' : 'loss',
          gameId,
        });
      }

      // Show winner/loser popup — persistent until user clicks OK
      await showGameAlert({
        variant: isCurrentPlayerWinner ? 'success' : 'error',
        title: isCurrentPlayerWinner ? 'You did it!' : 'Game Over! You lost.',
        message: isCurrentPlayerWinner
          ? 'All your tokens made it home first. What a fantastic win!'
          : 'Better luck next time—plan your moves carefully and try again!',
        allowBackdropClose: false,
        okText: 'OK',
        alertDecoration: isCurrentPlayerWinner ? 'trophy' : undefined,
      });

      // Navigate after user dismisses popup
      const isLiveMultiplayerMode =
        isLiveMode && gameState?.gameMode === 'multiplayer';

      if (isLiveComputerMode) {
        await handleLiveComputerCompletionFlow();
      } else if (isLiveMultiplayerMode) {
        handleLiveMultiplayerCompletionFlow(
          apiResult?.tournamentId || urlConfig.tournamentId
        );
      } else {
        const tid = apiResult?.tournamentId || urlConfig.tournamentId;
        if (tid) {
          window.location.href = `/tournament-structure/${tid}`;
        } else {
          navigate('/thankyou');
        }
      }
    };

    runWinnerFlow();
  }, [
    gameState?.winner,
    gameState?.abandoned,
    gameId,
    gameState?.gameMode,
    playerColor,
    gameState?.players,
    closeGamePopup,
    showGameAlert,
    navigate,
    isLiveMode,
    urlConfig.tournamentId,
    handleLiveComputerCompletionFlow,
  ]);

  // Handle edge cases for URL / local color vs existing game data
  useEffect(() => {
    if (!gameState || !gameState.players) return;

    const existingColors = Object.keys(gameState.players);

    // If playerColor is invalid or missing, try to pick a fallback color
    if (!playerColor || !COLORS.includes(playerColor)) {
      const available = COLORS.filter((c) => !existingColors.includes(c));
      if (available.length > 0) {
        const fallback = available[0];
        console.warn(
          '[Ludo] Player color missing/invalid, assigning fallback color:',
          fallback
        );
        setPlayerColor(fallback);
        localStorage.setItem('ludoPlayerColor', fallback);
      } else {
        console.warn('[Ludo] No available colors left in this game.');
      }
      return;
    }
  }, [gameState, playerColor]);

  // Advance turn atomically when timer has truly expired.
  // Any client can attempt this safely; only one transaction will win.
  const advanceTurnIfExpired = async () => {
    if (!gameId || !database) return;

    const gameRef = ref(database, `games/${gameId}`);
    try {
      await runTransaction(
        gameRef,
        (current) => {
          if (!current || current.winner || current.abandoned || !current.started || !current.turnStartTime || !current.players) {
            return current;
          }

          const turnLimit = current.gameMode === 'computer' ? 15 : 30;
          const elapsed = Math.floor((Date.now() - current.turnStartTime) / 1000);
          if (elapsed < turnLimit) return current;

          const colorsOrder = Object.keys(current.players);
          if (colorsOrder.length === 0) return current;

          if (current.gameMode === 'computer') {
            const idx = colorsOrder.indexOf(current.currentTurn);
            const safeIdx = idx >= 0 ? idx : 0;
            const next = colorsOrder[(safeIdx + 1) % colorsOrder.length];
            return {
              ...current,
              currentTurn: next,
              diceValue: null,
              lastRoll: null,
              turnStartTime: Date.now(),
              lastActivity: Date.now(),
            };
          }

          // Multiplayer: record missed turn, possibly disable player, then advance or end game
          const cur = current.currentTurn;
          const player = current.players[cur];
          if (!player) return current;

          const prevMissed = player.missedTurns ?? 0;
          const newMissed = Math.min(3, prevMissed + 1);
          const updatedPlayers = { ...current.players };
          updatedPlayers[cur] = {
            ...player,
            missedTurns: newMissed,
            disabled: newMissed >= 3 ? true : (player.disabled ?? false),
          };

          const activeColors = colorsOrder.filter((c) => !updatedPlayers[c].disabled);

          if (activeColors.length === 1) {
            return {
              ...current,
              players: updatedPlayers,
              winner: activeColors[0],
              abandoned: true,
              diceValue: null,
              lastRoll: null,
              turnStartTime: null,
              lastActivity: Date.now(),
            };
          }

          const curIdx = colorsOrder.indexOf(cur);
          let next = null;
          for (let i = 1; i <= colorsOrder.length; i++) {
            const candidate = colorsOrder[(curIdx + i) % colorsOrder.length];
            if (!updatedPlayers[candidate].disabled) {
              next = candidate;
              break;
            }
          }
          if (next == null) return current;

          return {
            ...current,
            players: updatedPlayers,
            currentTurn: next,
            diceValue: null,
            lastRoll: null,
            turnStartTime: Date.now(),
            lastActivity: Date.now(),
          };
        },
        { applyLocally: false }
      );
    } catch (err) {
      console.error('[Ludo] Failed to advance timed-out turn:', err);
    }
  };

  // Timer effect
  useEffect(() => {
    const TURN_LIMIT = gameState?.gameMode === 'computer' ? 15 : 30;

    if (!gameState || gameState.winner || gameState.abandoned || !gameState.started || !gameState.turnStartTime) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTurnTimer(gameState?.winner ? 0 : TURN_LIMIT);
      return;
    }

    const isMyTurn = gameState.currentTurn === playerColor;
    const currentTurnColor = gameState.currentTurn;
    const opponent = gameState.players?.[currentTurnColor];
    const isOpponentOffline = !isMyTurn && opponent && !opponent.isAI && opponent.online === false;
    const turnStartTime = gameState.turnStartTime;
    const elapsed = Math.floor((Date.now() - turnStartTime) / 1000);
    const remaining = Math.max(0, Math.min(TURN_LIMIT, TURN_LIMIT - elapsed));

    setTurnTimer(remaining);

    if (remaining === 0 && !isDiceRolling) {
      advanceTurnIfExpired();
      return;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      const currentElapsed = Math.floor((Date.now() - turnStartTime) / 1000);
      const currentRemaining = Math.max(0, Math.min(TURN_LIMIT, TURN_LIMIT - currentElapsed));
      
      setTurnTimer(currentRemaining);

      if (currentRemaining === 0 && !isDiceRolling) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        advanceTurnIfExpired();
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameState?.currentTurn, gameState?.started, gameState?.turnStartTime, playerColor, gameState?.players, isDiceRolling]);

  // Stop all background timers when game ends
  useEffect(() => {
    if (!gameState?.winner && !gameState?.abandoned) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (aiTurnTimeoutRef.current) {
      clearTimeout(aiTurnTimeoutRef.current);
      aiTurnTimeoutRef.current = null;
    }
    setMovablePieces([]);
  }, [gameState?.winner, gameState?.abandoned]);

  // Keep lastActivity ref in sync so the abandonment interval always reads fresh values
  useEffect(() => {
    lastActivityRef.current = gameState?.lastActivity || gameState?.turnStartTime || null;
    lastActivePlayerRef.current = gameState?.lastActivePlayer || null;
  }, [gameState?.lastActivity, gameState?.turnStartTime, gameState?.lastActivePlayer]);

  // Abandonment check — end game if no dice roll for 2 minutes (multiplayer only)
  useEffect(() => {
    if (!gameState || !gameState.started || gameState.winner || gameState.abandoned || gameState.gameMode === 'computer') {
      if (abandonmentCheckRef.current) {
        clearInterval(abandonmentCheckRef.current);
        abandonmentCheckRef.current = null;
      }
      return;
    }

    if (abandonmentCheckRef.current) {
      clearInterval(abandonmentCheckRef.current);
    }

    abandonmentCheckRef.current = setInterval(async () => {
      const activity = lastActivityRef.current;
      if (!activity) return;

      if (Date.now() - activity >= 120000) {
        clearInterval(abandonmentCheckRef.current);
        abandonmentCheckRef.current = null;
        const winner = lastActivePlayerRef.current || null;
        await update(ref(database, `games/${gameId}`), {
          abandoned: true,
          winner: winner,
        });
      }
    }, 10000);

    return () => {
      if (abandonmentCheckRef.current) {
        clearInterval(abandonmentCheckRef.current);
        abandonmentCheckRef.current = null;
      }
    };
  }, [gameState?.started, gameState?.winner, gameState?.abandoned, gameState?.gameMode, gameId]);

  // Show popup and redirect when game is abandoned
  useEffect(() => {
    if (!gameState?.abandoned || !gameId) return;
    if (abandonmentNotifiedRef.current) return;

    abandonmentNotifiedRef.current = true;

    // Stop all timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (aiTurnTimeoutRef.current) {
      clearTimeout(aiTurnTimeoutRef.current);
      aiTurnTimeoutRef.current = null;
    }
    if (abandonmentCheckRef.current) {
      clearInterval(abandonmentCheckRef.current);
      abandonmentCheckRef.current = null;
    }
    setMovablePieces([]);

    const abandonWinner = gameState.winner || gameState.lastActivePlayer || null;
    const abandonWinnerName = abandonWinner
      ? (gameState.players?.[abandonWinner]?.name || abandonWinner.toUpperCase())
      : null;
    const youWon = abandonWinner === playerColor;
    const isLobbyTimeout = gameState.lobbyTimeout === true;

    // Notify backend (skip for lobby timeout — already notified in lobby timer effect)
    if (!isLobbyTimeout) {
      notifyWinnerApi(gameId, abandonWinner || 'abandoned', gameState?.gameMode || 'multiplayer', 'abandonment', urlConfig.tournamentId);
    }
    if (isLiveMode) {
      const winnerId = abandonWinner ? (gameState?.players?.[abandonWinner]?.userId || null) : null;
      notifyLiveGameEnd(gameId, winnerId, 'abandonment');
    }

    const isLiveComputerMode = isLiveMode && gameState?.gameMode === 'computer';
    if (!isLiveComputerMode) {
      setLudoGameCompletedIfSinglePlay({ outcome: 'abandonment', gameId });
    }

    void (async () => {
      let alertOpts;
      if (isLobbyTimeout && youWon) {
        alertOpts = {
          variant: 'success',
          title: 'You win by default',
          message: 'No opponents joined in time. You advance in the tournament.',
        };
      } else if (abandonWinnerName) {
        alertOpts = {
          variant: youWon ? 'success' : 'warning',
          title: youWon ? 'You win' : 'Game abandoned',
          message: youWon ? (
            <>
              The game ended due to inactivity or skipped turns.
              <br />
              You win as the last active player.
            </>
          ) : (
            <>
              The game ended due to inactivity or 3 skipped turns.
              <br />
              <strong>{abandonWinnerName}</strong> wins.
            </>
          ),
        };
      } else {
        alertOpts = {
          variant: 'warning',
          title: 'Game abandoned',
          message: (
            <>
              The game ended due to inactivity.
              <br />
              No winner was decided.
            </>
          ),
        };
      }
      await showGameAlert(alertOpts);
      const isLiveMultiplayerMode =
        isLiveMode && gameState?.gameMode === 'multiplayer';

      if (isLiveComputerMode) {
        await handleLiveComputerCompletionFlow();
      } else if (isLiveMultiplayerMode) {
        handleLiveMultiplayerCompletionFlow(urlConfig.tournamentId);
      } else if (urlConfig.tournamentId) {
        window.location.href = `/tournament-structure/${urlConfig.tournamentId}`;
      } else {
        navigate('/thankyou');
      }
    })();
  }, [
    gameState?.abandoned,
    gameId,
    gameState?.gameMode,
    gameState?.winner,
    gameState?.lastActivePlayer,
    gameState?.players,
    playerColor,
    showGameAlert,
    navigate,
    isLiveMode,
    urlConfig.tournamentId,
    handleLiveComputerCompletionFlow,
  ]);

  // Reset tournament auto-start ref when gameId changes (e.g. new game)
  useEffect(() => {
    tournamentAutoStartAttemptedRef.current = false;
  }, [gameId]);

  // Tournament: auto-start once 2 players have joined (do not wait for full 3/4 roster).
  // Remaining scheduled players can still join after started: true.
  useEffect(() => {
    if (!urlConfig.tournamentId || !gameState?.players || gameState?.started) return;
    const minPlayers = getMinPlayersToStart(urlConfig.gameType, { tournament: true });
    if (Object.keys(gameState.players).length < minPlayers) return;
    if (tournamentAutoStartAttemptedRef.current) return;

    tournamentAutoStartAttemptedRef.current = true;
    startGame();
  }, [urlConfig.tournamentId, urlConfig.gameType, gameState?.players, gameState?.started]);

  // Tournament lobby: 10-minute timer; if only 1 player when it expires, declare them winner (default win)
  useEffect(() => {
    const isLobbyTimerActive =
      urlConfig.tournamentId &&
      gameId &&
      gameState?.players &&
      Object.keys(gameState.players).length === 1 &&
      !gameState?.started &&
      !gameState?.winner &&
      !gameState?.abandoned;

    if (!isLobbyTimerActive) {
      if (lobbyTimerIntervalRef.current) {
        clearInterval(lobbyTimerIntervalRef.current);
        lobbyTimerIntervalRef.current = null;
      }
      setLobbyTimerSeconds(600);
      return;
    }

    if (lobbyTimerIntervalRef.current) return;
    setLobbyTimerSeconds(600);

    const applyLobbyDefaultWin = () => {
      const gameRef = ref(database, `games/${gameId}`);
      runTransaction(
        gameRef,
        (current) => {
          if (!current || current.winner || current.abandoned || current.started) return current;
          const players = current.players || {};
          if (Object.keys(players).length !== 1) return current;
          const winnerColor = Object.keys(players)[0];
          return {
            ...current,
            winner: winnerColor,
            abandoned: true,
            lobbyTimeout: true,
          };
        },
        { applyLocally: false }
      ).then((result) => {
        const val = result.snapshot.val();
        if (val && val.winner && val.lobbyTimeout) {
          notifyWinnerApi(gameId, val.winner, val.gameMode || 'multiplayer', 'win', urlConfig.tournamentId);
        }
      }).catch((err) => {
        console.error('[Ludo] Lobby default win failed:', err);
      });
    };

    lobbyTimerIntervalRef.current = setInterval(() => {
      setLobbyTimerSeconds((prev) => {
        if (prev <= 1) {
          if (lobbyTimerIntervalRef.current) {
            clearInterval(lobbyTimerIntervalRef.current);
            lobbyTimerIntervalRef.current = null;
          }
          setTimeout(applyLobbyDefaultWin, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (lobbyTimerIntervalRef.current) {
        clearInterval(lobbyTimerIntervalRef.current);
        lobbyTimerIntervalRef.current = null;
      }
    };
  }, [urlConfig.tournamentId, gameId, gameState?.started, gameState?.players, gameState?.winner, gameState?.abandoned]);

  // AI Turn Handler
  useEffect(() => {
    if (!gameState || gameState.winner || gameState.abandoned || !gameState.started || !gameState.players) return;

    const currentPlayer = gameState.players[gameState.currentTurn];
    if (!currentPlayer || !currentPlayer.isAI) {
      if (aiTurnTimeoutRef.current) {
        clearTimeout(aiTurnTimeoutRef.current);
        aiTurnTimeoutRef.current = null;
      }
      return;
    }

    // Only trigger AI if dice hasn't been rolled yet
    if (gameState.diceValue) {
      return;
    }

    // AI's turn - execute after a short delay
    if (aiTurnTimeoutRef.current) {
      clearTimeout(aiTurnTimeoutRef.current);
    }

    aiTurnTimeoutRef.current = setTimeout(() => {
      makeAIMove(gameState.currentTurn);
    }, gameState?.gameMode === 'computer' ? 500 : 1000);

    return () => {
      if (aiTurnTimeoutRef.current) {
        clearTimeout(aiTurnTimeoutRef.current);
        aiTurnTimeoutRef.current = null;
      }
    };
  }, [gameState?.currentTurn, gameState?.started, gameState?.diceValue]);

  // Play notification sound when it becomes user's turn
  useEffect(() => {
    if (!gameState || !gameState.started || !playerColor) return;
    
    const currentTurn = gameState.currentTurn;
    const prevTurn = prevTurnRef.current;
    
    // Play sound + show indicator when turn changes to current player AND it's not an AI
    if (currentTurn === playerColor && prevTurn !== currentTurn && prevTurn !== null) {
      const currentPlayer = gameState.players?.[playerColor];
      if (currentPlayer && !currentPlayer.isAI) {
        playTurnNotification();
        setShowYourTurnIndicator(true);
        setTimeout(() => setShowYourTurnIndicator(false), 2000);
      }
    }
    
    // Update previous turn
    prevTurnRef.current = currentTurn;
  }, [gameState?.currentTurn, gameState?.started, playerColor]);

  useEffect(() => {
    const computeBoardSize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const size = Math.min(560, Math.floor(vw * 0.85), Math.floor(vh * 0.55));
      return Math.max(260, size);
    };

    const updateSize = () => {
      const next = computeBoardSize();
      if (boardSizeRef.current !== next) {
        boardSizeRef.current = next;
        setBoardSize(next);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  }, []);

  const checkMovablePieces = (state) => {
    if (!state || !state.players || !state.players[playerColor]) return;

    const movable = [];
    const pieces = state.players[playerColor].pieces;
    const dice = state.diceValue;

    pieces.forEach((pos, idx) => {
      if (canMovePiece(pos, dice)) {
        movable.push(idx);
      }
    });

    setMovablePieces(movable);
  };

  const canMovePiece = (position, dice, color = playerColor) => {
    if (!dice) return false;
    if (!isValidPosition(color, position)) return false;
    if (position === CENTER_POSITION) return false; // already finished
    if (position === 0) return dice === 6;
    if (position > 0 && position + dice > CENTER_POSITION) return false;
    if (position >= 1 && position <= MAIN_PATH_LENGTH) {
      const entryPos = ENTRY_POSITIONS[color] || MAIN_PATH_LENGTH;
      const target = position + dice;
      if (position <= entryPos && target > entryPos) {
        const stepsIntoHome = target - entryPos;
        return MAIN_PATH_LENGTH + stepsIntoHome <= CENTER_POSITION;
      }
    }
    return true;
  };

  const createGame = async () => {
    const resolvedName = playerName.trim() || generateGuestName();

    if (!database) {
      showGameAlert({
        variant: 'error',
        title: 'Configuration Error',
        message: 'Firebase not configured! Please add your Firebase config.',
      });
      return;
    }

    try {
      const newGameRef = push(ref(database, 'games'));
      const newGameId = newGameRef.key;

      const players = {};
      let playerColorChoice;
      let firstTurn;

      if (gameMode === 'computer') {
        // Computer mode: require user to select a color
        if (!selectedColor) {
          showGameAlert({
            variant: 'warning',
            title: 'Select a Color',
            message: 'Please choose your color before starting the game.',
          });
          return;
        }
        playerColorChoice = selectedColor;
        firstTurn = playerColorChoice;
        
        players[playerColorChoice] = { 
          name: resolvedName, 
          pieces: [0, 0, 0, 0], 
          finished: 0, 
          isAI: false,
          userId: urlConfig.userId || null,
        };

        // Add AI opponents from remaining colors
        const remainingColors = COLORS.filter(c => c !== playerColorChoice);
        const aiColors = shuffleArray(remainingColors).slice(0, aiOpponentCount);
        aiColors.forEach((color, index) => {
          players[color] = { 
            name: `Computer ${index + 1}`, 
            pieces: [0, 0, 0, 0], 
            finished: 0, 
            isAI: true 
          };
        });
      } else {
        // Multiplayer mode
        if (isLiveMode) {
          if (!selectedColor) {
            showGameAlert({
              variant: 'warning',
              title: 'Select a Color',
              message: 'Please choose your color before creating a game.',
            });
            return;
          }
          playerColorChoice = selectedColor;
        } else {
          const shuffledColors = shuffleArray(COLORS);
          playerColorChoice = shuffledColors[0];
        }
        firstTurn = playerColorChoice;
        
        players[playerColorChoice] = { 
          name: resolvedName, 
          pieces: [0, 0, 0, 0], 
          finished: 0, 
          isAI: false,
          userId: urlConfig.userId || null,
          missedTurns: 0,
          disabled: false,
        };
      }

      const initialState = {
        players: players,
        currentTurn: firstTurn,
        started: false,
        diceValue: null,
        winner: null,
        lastRoll: null,
        turnStartTime: null,
        gameMode: gameMode,
        colorOrder: gameMode === 'computer' ? Object.keys(players) : null // Store color order for turn logic
      };

      await set(newGameRef, initialState);

      localStorage.setItem('ludoGameId', newGameId);
      localStorage.setItem('ludoPlayerName', resolvedName);
      localStorage.setItem('ludoPlayerColor', playerColorChoice);

      setGameId(newGameId);
      setPlayerColor(playerColorChoice);
      setPlayerName(resolvedName);
      setShowJoinCreate(false);

      // Auto-start if computer mode
      if (gameMode === 'computer') {
        if (isLiveMode) {
          notifyLiveGameStart(newGameId, players);
        }
        setTimeout(async () => {
          await update(ref(database, `games/${newGameId}`), { 
            started: true,
            turnStartTime: Date.now(),
            lastActivity: Date.now(),
          });
        }, 1000);
      }
    } catch (err) {
      console.error("Create game error:", err);
      showGameAlert({
        variant: 'error',
        title: 'Error',
        message: 'Failed to create game. Check Firebase configuration.',
      });
    }
  };

  const joinGame = async () => {
    if (!joinGameId.trim()) {
      showGameAlert({
        variant: 'warning',
        title: 'Missing Game ID',
        message: 'Please enter game ID!',
      });
      return;
    }

    if (!database) {
      showGameAlert({
        variant: 'error',
        title: 'Configuration Error',
        message: 'Firebase not configured!',
      });
      return;
    }

    try {
      const resolvedName = playerName.trim() || generateGuestName();
      const gameRef = ref(database, `games/${joinGameId}`);
      const snapshot = await new Promise((resolve) => {
        onValue(gameRef, resolve, { onlyOnce: true });
      });

      const game = snapshot.val();
      if (!game) {
        showGameAlert({
          variant: 'error',
          title: 'Game Not Found',
          message: 'Game not found!',
        });
        return;
      }

      if (game.winner || game.abandoned) {
        showGameAlert({
          variant: 'warning',
          title: 'Game Ended',
          message: 'This game has already ended.',
        });
        return;
      }

      // Check if this user already has a seat in the game (rejoin by userId)
      // Late join while started: true is allowed; turn order uses current players keys.
      const currentUserId = urlConfig.userId;
      if (currentUserId && game.players) {
        const match = Object.entries(game.players).find(
          ([, p]) => p.userId === currentUserId
        );
        if (match) {
          const [existingColor, existingPlayer] = match;
          localStorage.setItem('ludoGameId', joinGameId);
          localStorage.setItem('ludoPlayerName', existingPlayer.name || resolvedName);
          localStorage.setItem('ludoPlayerColor', existingColor);

          setGameId(joinGameId);
          setPlayerColor(existingColor);
          setPlayerName(existingPlayer.name || resolvedName);
          setShowJoinCreate(false);
          return;
        }
      }

      const availableColors = COLORS.filter(c => !game.players[c]);
      if (availableColors.length === 0) {
        showGameAlert({
          variant: 'warning',
          title: 'Game Full',
          message: 'Game is full!',
        });
        return;
      }

      let color;
      if (isLiveMode) {
        if (!selectedColor) {
          showGameAlert({
            variant: 'warning',
            title: 'Select a Color',
            message: 'Please choose your color before joining.',
          });
          return;
        }
        if (!availableColors.includes(selectedColor)) {
          showGameAlert({
            variant: 'warning',
            title: 'Color Taken',
            message: `${selectedColor.charAt(0).toUpperCase() + selectedColor.slice(1)} is already taken. Please choose another color.`,
          });
          setTakenColors(COLORS.filter(c => !availableColors.includes(c)));
          return;
        }
        color = selectedColor;
      } else {
        color = availableColors[Math.floor(Math.random() * availableColors.length)];
      }

      await update(gameRef, {
        [`players/${color}`]: { name: resolvedName, pieces: [0, 0, 0, 0], finished: 0, isAI: false, userId: currentUserId || null, missedTurns: 0, disabled: false }
      });

      localStorage.setItem('ludoGameId', joinGameId);
      localStorage.setItem('ludoPlayerName', resolvedName);
      localStorage.setItem('ludoPlayerColor', color);

      setGameId(joinGameId);
      setPlayerColor(color);
      setPlayerName(resolvedName);
      setShowJoinCreate(false);
    } catch (err) {
      console.error("Join game error:", err);
      showGameAlert({
        variant: 'error',
        title: 'Error',
        message: 'Failed to join game.',
      });
    }
  };

  const startGame = async () => {
    if (!gameState || !gameState.players) return;

    const minPlayers = getMinPlayersToStart(urlConfig.gameType, {
      tournament: Boolean(urlConfig.tournamentId),
    });
    if (Object.keys(gameState.players).length < minPlayers) {
      showGameAlert({
        variant: 'warning',
        title: 'Not Enough Players',
        message: `Need at least ${minPlayers} players to start!`,
      });
      return;
    }
    if (isLiveMode) {
      notifyLiveGameStart(gameId, gameState.players);
    }
    await update(ref(database, `games/${gameId}`), { 
      started: true,
      turnStartTime: Date.now(),
      lastActivity: Date.now(),
    });
  };

  const rollDice = async () => {
    if (!gameState || gameState.winner || gameState.abandoned || gameState.currentTurn !== playerColor || gameState.diceValue || isDiceRolling) return;

    const finalDice = Math.floor(Math.random() * 6) + 1;
    const player = gameState.players[playerColor];
    const pieces = player.pieces;
    const hasMove = pieces.some(pos => canMovePiece(pos, finalDice));

    // Dice roll animation: show random values for ~800ms then settle on final
    setIsDiceRolling(true);
    setDiceDisplayValue(null);

    const rollDuration = 800;
    const tickInterval = 80;
    const startTime = Date.now();
    const rollInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= rollDuration) {
        clearInterval(rollInterval);
        setDiceDisplayValue(finalDice);
        setIsDiceRolling(false);
        return;
      }
      setDiceDisplayValue(Math.floor(Math.random() * 6) + 1);
    }, tickInterval);

    // Update Firebase after animation finishes
    setTimeout(async () => {
      if (!hasMove) {
        await update(ref(database, `games/${gameId}`), {
          diceValue: finalDice,
          lastRoll: Date.now(),
          lastActivity: Date.now(),
          lastActivePlayer: playerColor,
        });
        setTimeout(async () => {
          await nextTurn();
        }, 800);
        return;
      }
      await update(ref(database, `games/${gameId}`), {
        diceValue: finalDice,
        lastRoll: Date.now(),
        turnStartTime: Date.now(),
        lastActivity: Date.now(),
        lastActivePlayer: playerColor,
      });
    }, rollDuration);
  };

  const nextTurn = async () => {
    if (!gameState || !gameState.players || gameState.winner || gameState.abandoned) return;

    const colorsOrder = Object.keys(gameState.players);
    const activeColors = colorsOrder.filter((c) => !gameState.players[c].disabled);
    if (activeColors.length === 0) return;

    const idx = colorsOrder.indexOf(gameState.currentTurn);
    let next = null;
    for (let i = 1; i <= colorsOrder.length; i++) {
      const candidate = colorsOrder[(idx + i) % colorsOrder.length];
      if (!gameState.players[candidate].disabled) {
        next = candidate;
        break;
      }
    }
    if (next == null) return;

    await update(ref(database, `games/${gameId}`), {
      currentTurn: next,
      diceValue: null,
      lastRoll: null,
      turnStartTime: Date.now()
    });

    setMovablePieces([]);
  };

  const movePiece = async (pieceIndex) => {
    if (
      !movablePieces.includes(pieceIndex) ||
      !gameState ||
      isMoving ||
      gameState.winner ||
      gameState.abandoned ||
      gameState.currentTurn !== playerColor ||
      !gameState.diceValue ||
      !gameState.lastRoll
    ) {
      return;
    }

    setIsMoving(true);

    const currentPieces = [...gameState.players[playerColor].pieces];
    const currentPos = currentPieces[pieceIndex];
    const dice = gameState.diceValue;

    console.log(`[MOVE] ${playerColor} piece ${pieceIndex}: position ${currentPos} + dice ${dice}`);

    if (!Number.isInteger(dice) || !Number.isInteger(currentPos)) {
      console.warn(`[MOVE] Invalid move data: position=${currentPos}, dice=${dice}`);
      setIsMoving(false);
      return;
    }

    let newPos;

    if (currentPos === 0) {
      newPos = 1;
      console.log(`[MOVE] Starting from home, going to position 1`);
    } else if (currentPos >= 1 && currentPos <= MAIN_PATH_LENGTH) {
      const entryPos = ENTRY_POSITIONS[playerColor] || MAIN_PATH_LENGTH;
      const target = currentPos + dice;

      console.log(`[MOVE] Entry logic: currentPos=${currentPos}, entryPos=${entryPos}, target=${target}`);

      if (currentPos <= entryPos && target > entryPos) {
        // Crossing into home stretch after reaching entry square
        const stepsIntoHome = target - entryPos;
        newPos = MAIN_PATH_LENGTH + stepsIntoHome;
        console.log(`[MOVE] Entering home stretch: stepsIntoHome=${stepsIntoHome}, newPos=${newPos}`);
      } else {
        // Normal move on main path (including landing on entry square)
        newPos = Math.min(target, MAIN_PATH_LENGTH);
      }

      console.log(`[MOVE] Moving from ${currentPos} to ${newPos}`);
    } else if (currentPos > MAIN_PATH_LENGTH && currentPos <= CENTER_POSITION) {
      // Already in home stretch
      newPos = Math.min(currentPos + dice, CENTER_POSITION);
      console.log(`[MOVE] Moving within home stretch from ${currentPos} to ${newPos}`);
    } else {
      return; // invalid state
    }

    if (!Number.isInteger(newPos)) {
      console.warn(`[MOVE] Invalid new position: ${newPos}`);
      setIsMoving(false);
      return;
    }

    if (!isValidPosition(playerColor, newPos)) {
      console.warn(`[MOVE] Illegal position for ${playerColor}: ${newPos}`);
      setIsMoving(false);
      return;
    }

    currentPieces[pieceIndex] = newPos;
    console.log(`[MOVE] Final position for piece ${pieceIndex}: ${newPos}`);

    // Check for captures and track if we captured (extra turn)
    let didCapture = false;
    const captureEnemyUpdates = [];
    if (newPos >= 1 && newPos <= MAIN_PATH_LENGTH) {
      const safeCells = new Set([
        "1,6","8,1","13,8","6,13",   // starting squares
        "6,2","2,8","8,12","12,6"   // star/safe squares
      ]);

      const playerPath = PATHS[playerColor];
      if (!playerPath) {
        console.warn(`[CAPTURE] Missing path for ${playerColor}`);
      } else {
        const myCell = playerPath[newPos - 1];
        if (!myCell) {
          console.warn(`[CAPTURE] Invalid cell for ${playerColor} position ${newPos}`);
        } else {
          const myKey = `${myCell.x},${myCell.y}`;

          if (!safeCells.has(myKey)) {
            for (const color of Object.keys(gameState.players)) {
              if (color === playerColor) continue;

              const prevPieces = gameState.players[color].pieces;
              const enemyPieces = prevPieces.map((pos) => {
                if (typeof pos !== 'number' || pos < 1 || pos > MAIN_PATH_LENGTH) {
                  return pos;
                }

                const enemyPath = PATHS[color];
                if (!enemyPath) {
                  return pos;
                }

                const enemyCell = enemyPath[pos - 1];
                if (!enemyCell) {
                  return pos;
                }

                const enemyKey = `${enemyCell.x},${enemyCell.y}`;

                if (enemyKey === myKey) {
                  console.log(`[CAPTURE] ${playerColor} captured ${color}`);
                  didCapture = true;
                  return 0;
                }
                return pos;
              });

              if (enemyPieces.some((p, i) => p !== prevPieces[i])) {
                captureEnemyUpdates.push({ color, pieces: enemyPieces });
              }
            }
          }
        }
      }
    }

    const reachedCenter = newPos === CENTER_POSITION;
    const finished = currentPieces.filter(p => p === CENTER_POSITION).length;

    await update(ref(database, `games/${gameId}/players/${playerColor}`), {
      pieces: currentPieces,
      finished: finished
    });

    if (didCapture && captureEnemyUpdates.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, PIECE_MOVE_TRANSITION_MS));
      for (const { color: enemyColor, pieces } of captureEnemyUpdates) {
        await update(ref(database, `games/${gameId}/players/${enemyColor}`), {
          pieces
        });
      }
    }

    if (finished === 4) {
      await update(ref(database, `games/${gameId}`), {
        winner: playerColor
      });
      // Inform tournament backend about the winner
      notifyWinnerApi(gameId, playerColor, gameState?.gameMode || 'multiplayer', 'win', urlConfig.tournamentId);
      if (isLiveMode) {
        const winnerId = gameState?.players?.[playerColor]?.userId || null;
        notifyLiveGameEnd(gameId, winnerId, 'win');
      }
      setIsMoving(false);
      return;
    }

    // Extra turn: on 6, or when piece reaches center, or when capturing an opponent
    const extraTurn = dice === 6 || reachedCenter || didCapture;
    if (!extraTurn) {
      await nextTurn();
    } else {
      await update(ref(database, `games/${gameId}`), {
        diceValue: null,
        lastRoll: null
      });
      setMovablePieces([]);
    }

    setIsMoving(false);
  };

  const getPiecePosition = (color, pieceIndex) => {
    if (!gameState || !gameState.players || !gameState.players[color]) {
      return { x: 0, y: 0 };
    }

    const position = gameState.players[color].pieces[pieceIndex];
    const cellSize = boardSize / 15;

    // DEBUG: Log what we're calculating
    console.log(`[DEBUG] ${color} piece ${pieceIndex}: position=${position}`);

    const homes = {
      green: [
        { x: 2, y: 2 }, { x: 4, y: 2 },
        { x: 2, y: 4 }, { x: 4, y: 4 }
      ],
      yellow: [
        { x: 11, y: 2 }, { x: 13, y: 2 },
        { x: 11, y: 4 }, { x: 13, y: 4 }
      ],
      blue: [
        { x: 11, y: 11 }, { x: 13, y: 11 },
        { x: 11, y: 13 }, { x: 13, y: 13 }
      ],
      red: [
        { x: 2, y: 11 }, { x: 4, y: 11 },
        { x: 2, y: 13 }, { x: 4, y: 13 }
      ]
    };

    if (!Number.isInteger(position)) {
      const fallback = homes[color]?.[pieceIndex];
      if (fallback) {
        return { x: fallback.x * cellSize, y: fallback.y * cellSize };
      }
      return { x: 0, y: 0 };
    }

    // Home positions
    if (position === 0) {
      const pos = homes[color][pieceIndex];
      return { x: pos.x * cellSize, y: pos.y * cellSize };
    }

    // Check if in home stretch first
    if (position >= MAIN_PATH_LENGTH + 1 && position <= CENTER_POSITION) {
      const homeIndex = position - (MAIN_PATH_LENGTH + 1);
      const actualPos = HOME_STRETCHES[color]?.[homeIndex];
      if (actualPos) {
        return { x: (actualPos.x + 0.5) * cellSize, y: (actualPos.y + 0.5) * cellSize };
      }
    }

    if (position === CENTER_POSITION) {
      // Place each color's finished pieces at the centroid of its triangle
      // Center area spans cells 6-8 (3×3), center point at (7.5, 7.5)
      // Triangle centroids sit 1 cell away from center toward each side
      const triangleCenters = {
        green:  { x: 6.5, y: 7.5 },  // left triangle
        yellow: { x: 7.5, y: 6.5 },  // top triangle
        blue:   { x: 8.5, y: 7.5 },  // right triangle
        red:    { x: 7.5, y: 8.5 },  // bottom triangle
      };
      // Small 2×2 spread so up to 4 finished pieces don't stack on one point
      const spread = cellSize * 0.22;
      const microOffsets = [
        { dx: -spread, dy: -spread },
        { dx:  spread, dy: -spread },
        { dx: -spread, dy:  spread },
        { dx:  spread, dy:  spread },
      ];
      const tc = triangleCenters[color];
      const mo = microOffsets[pieceIndex] || { dx: 0, dy: 0 };
      return {
        x: tc.x * cellSize + mo.dx,
        y: tc.y * cellSize + mo.dy,
      };
    }

    // Main path - FIXED with correct starting squares on white path
    // Grid: rows 0-14, cols 0-14
    // Home areas: Green(0-5,0-5), Yellow(0-5,9-14), Blue(9-14,9-14), Red(9-14,0-5)


    if (position >= 1 && position <= MAIN_PATH_LENGTH) {
      const actualPos = PATHS[color][position - 1];

      if (actualPos) {
        return {
          x: (actualPos.x + 0.5) * cellSize,
          y: (actualPos.y + 0.5) * cellSize
        };
      }
    }


    console.log(`[DEBUG] ${color} piece ${pieceIndex} - NO VALID POSITION, returning center`);
    return { x: 280, y: 280 };
  };

  const leaveGame = () => {
    // Computer mode with active game: confirm then mark as loss, complete, redirect to /thankyou
    if (gameMode === 'computer' && gameId && gameState?.started) {
      showGameConfirm({
        title: 'Leave game?',
        message: 'If you leave, this will count as a loss.',
        confirmText: 'Leave',
        cancelText: 'Cancel',
      }).then(async (confirmed) => {
        if (confirmed) {
          const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userData') : null;
          const userData = stored ? JSON.parse(stored) : {};
          const sessionId = userData.sessionId || null;
          try {
            const res = await fetch(process.env.REACT_APP_FOLDER_LINK + '/api/save-computer-multiplayer.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId,
                gameId,
                status: 'completed',
                outcome: 'loss',
                winnerId: null,
              }),
            });
            const data = res.ok ? await res.json().catch(() => ({})) : {};
            if (data.send_report === true) {
              const reportData = {
                sessionId: userData.sessionId,
                role: userData.source,
                token: userData.token || null,
                name: userData.name,
                userId: userData.userId,
                gameId: userData.gameId || '',
                organizationId: userData.organizationId,
                points: 0,
                time: data.duration || '00:00:00',
                reportId: userData.reportId || null,
                ans: '',
              };
              sendReport(reportData)
                .then((apiRes) => console.log('Report API SUCCESS:', apiRes))
                .catch((err) => console.error('Report API FAILED:', err));
            }
          } catch (err) {
            console.error('Failed to save leave-as-loss', err);
          }

          if (isLiveMode && gameMode === 'computer') {
            await handleLiveComputerCompletionFlow();
          } else if (isLiveMode && gameMode === 'multiplayer') {
            setLudoGameCompletedIfSinglePlay({ outcome: 'loss', gameId });
            handleLiveMultiplayerCompletionFlow(urlConfig.tournamentId);
          } else {
            setLudoGameCompletedIfSinglePlay({ outcome: 'loss', gameId });
            localStorage.removeItem('ludoGameId');
            localStorage.removeItem('ludoPlayerName');
            localStorage.removeItem('ludoPlayerColor');
            setGameId(null);
            setPlayerColor(null);
            setShowJoinCreate(true);
            setGameState(null);
            navigate('/thankyou');
          }
        }
      });
      return;
    }
    localStorage.removeItem('ludoGameId');
    localStorage.removeItem('ludoPlayerName');
    localStorage.removeItem('ludoPlayerColor');
    setGameId(null);
    setPlayerColor(null);
    setShowJoinCreate(true);
    setGameState(null);
    if (urlConfig.tournamentId) {
      navigate(`/tournament-structure/${urlConfig.tournamentId}`);
    }
  };

  const renderGamePopupPortal = () => {
    if (!gamePopup) return null;
    const v = gamePopup.variant || 'info';

    if (gamePopup.kind === 'toast') {
      return createPortal(
        <div className="ludo-game-popup-portal-root">
          <div className="ludo-game-popup-toast-wrap">
            <div
              className={`ludo-game-popup-toast ludo-game-popup-toast--${gamePopup.variant}`}
            >
              {gamePopup.title}
            </div>
          </div>
        </div>,
        document.body
      );
    }

    if (gamePopup.kind === 'confirm') {
      return createPortal(
        <div
          className="ludo-game-popup-portal-root ludo-game-popup-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) finishGameConfirm(false);
          }}
          role="presentation"
        >
          <div
            className={`ludo-game-popup-card ludo-game-popup-card--${gamePopup.variant}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ludo-game-popup-confirm-title"
          >
            <div className="ludo-game-popup-dice-wrap" aria-hidden="true">
              <span className="ludo-game-popup-dice">🎲</span>
            </div>
            {gamePopup.title ? (
              <h2 id="ludo-game-popup-confirm-title" className="ludo-game-popup-title">
                {gamePopup.title}
              </h2>
            ) : null}
            <div
              className={
                'ludo-game-popup-message' +
                (!gamePopup.title ? ' ludo-game-popup-message--solo' : '')
              }
            >
              {gamePopup.message}
            </div>
            <div className="ludo-game-popup-actions">
              <button
                type="button"
                className="ludo-game-popup-cancel"
                onClick={() => finishGameConfirm(false)}
              >
                {gamePopup.cancelText}
              </button>
              <button
                type="button"
                className="ludo-game-popup-confirm-danger"
                onClick={() => finishGameConfirm(true)}
              >
                {gamePopup.confirmText}
              </button>
            </div>
          </div>
        </div>,
        document.body
      );
    }

    if (gamePopup.kind === 'alert') {
      const onBackdrop = (e) => {
        if (
          e.target === e.currentTarget &&
          gamePopup.allowBackdropClose
        ) {
          finishGameAlert();
        }
      };
      return createPortal(
        <div
          className="ludo-game-popup-portal-root ludo-game-popup-backdrop"
          onClick={onBackdrop}
          role="presentation"
        >
          <div
            className={`ludo-game-popup-card ludo-game-popup-card--${v}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={
              gamePopup.title ? 'ludo-game-popup-alert-title' : undefined
            }
          >
            <div className="ludo-game-popup-dice-wrap" aria-hidden="true">
              {gamePopup.alertDecoration === 'trophy' ? (
                <span className="ludo-game-popup-trophy">🏆</span>
              ) : (
                <span className="ludo-game-popup-dice">🎲</span>
              )}
            </div>
            {gamePopup.title ? (
              <h2
                id="ludo-game-popup-alert-title"
                className="ludo-game-popup-title"
              >
                {gamePopup.title}
              </h2>
            ) : null}
            <div
              className={
                'ludo-game-popup-message' +
                (!gamePopup.title ? ' ludo-game-popup-message--solo' : '')
              }
            >
              {gamePopup.message}
            </div>
            <div className="ludo-game-popup-actions">
              <button
                type="button"
                className={`ludo-game-popup-ok ludo-game-popup-ok--${v}`}
                onClick={finishGameAlert}
              >
                {gamePopup.okText}
              </button>
            </div>
          </div>
        </div>,
        document.body
      );
    }

    return null;
  };

  if (checkingExistingGame) {
    return (
      <>
      <div style={{
        fontFamily: "'Fredoka', sans-serif",
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            border: '4px solid rgba(102, 126, 234, 0.3)',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ margin: 0 }}>Checking for existing game...</p>
        </div>
      </div>
      {renderGamePopupPortal()}
    </>
    );
  }

  if (showJoinCreate) {
    return (
      <>
      <div
        className="landing-root"
        style={{
          fontFamily: "'Fredoka', sans-serif",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Bubblegum+Sans&family=Fredoka:wght@400;600&display=swap');
        `}</style>

        <div
          className="landing-card"
          style={{
            background: 'white',
            borderRadius: '22px',
            padding: '36px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            textAlign: 'center',
            maxWidth: '420px',
            width: '100%'
          }}
        >
          {/* <h1 style={{
            fontFamily: "'Bubblegum Sans', cursive",
            fontSize: '64px',
            color: '#6c7cff',
            marginBottom: '8px',
            textShadow: '3px 3px 0 #6a4bb3'
          }}>LUDO</h1> */}

          {/* <p style={{ fontSize: '14px', color: '#7a7a7a', marginBottom: '24px' }}>
            Classic Multiplayer Board Game
          </p> */}

          <div style={{ marginBottom: '22px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '8px',
              textAlign: 'left'
            }}>Game Mode</label>
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '16px'
            }}>
              {/* Non-live: show both. Live: show only if theme allows */}
              {(!isLiveMode || liveThemeFlags.multiplayer) && (
                <button
                  onClick={() => setGameMode('multiplayer')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: gameMode === 'multiplayer' ? '2px solid #6c7cff' : '2px solid #e7e7e7',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontFamily: "'Fredoka', sans-serif",
                    cursor: 'pointer',
                    background: gameMode === 'multiplayer' ? '#f0f2ff' : 'white',
                    color: gameMode === 'multiplayer' ? '#6c7cff' : '#666',
                    fontWeight: gameMode === 'multiplayer' ? '600' : '400',
                    transition: 'all 0.2s'
                  }}
                >
                  Multiplayer
                </button>
              )}
              {(!isLiveMode || liveThemeFlags.computer) && (
                <button
                  onClick={() => setGameMode('computer')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: gameMode === 'computer' ? '2px solid #6c7cff' : '2px solid #e7e7e7',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontFamily: "'Fredoka', sans-serif",
                    cursor: 'pointer',
                    background: gameMode === 'computer' ? '#f0f2ff' : 'white',
                    color: gameMode === 'computer' ? '#6c7cff' : '#666',
                    fontWeight: gameMode === 'computer' ? '600' : '400',
                    transition: 'all 0.2s'
                  }}
                >
                  Play vs Computer
                </button>
              )}
            </div>
          </div>

          {gameMode === 'computer' && (
            <div style={{ marginBottom: '22px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                color: '#333',
                marginBottom: '8px',
                textAlign: 'left'
              }}>Number of Opponents</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* Non-live: 1,2,3. Live: 1 always; 2 if computer_v3; 3 if computer_v4 */}
                {(isLiveMode
                  ? [1, ...(liveThemeFlags.computer_v3 ? [2] : []), ...(liveThemeFlags.computer_v4 ? [3] : [])]
                  : [1, 2, 3]
                ).map(count => (
                  <button
                    key={count}
                    onClick={() => setAiOpponentCount(count)}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      border: aiOpponentCount === count ? '2px solid #6c7cff' : '2px solid #e7e7e7',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontFamily: "'Fredoka', sans-serif",
                      cursor: 'pointer',
                      background: aiOpponentCount === count ? '#f0f2ff' : 'white',
                      color: aiOpponentCount === count ? '#6c7cff' : '#666',
                      fontWeight: aiOpponentCount === count ? '600' : '400',
                      transition: 'all 0.2s'
                    }}
                  >
                    {count} vs 1
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '22px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '8px',
              textAlign: 'left'
            }}>Your Name</label>
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              disabled
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createGame()}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e7e7e7',
                borderRadius: '12px',
                fontSize: '14px',
                fontFamily: "'Fredoka', sans-serif",
                outline: 'none',
                background: '#f5f5f5',
                cursor: 'not-allowed',
                opacity: 0.85,
                boxSizing: 'border-box'
              }}
            />
          </div>

          {(isLiveMode || gameMode === 'computer') && (
            <div style={{ marginBottom: '22px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                color: '#333',
                marginBottom: '8px',
                textAlign: 'left'
              }}>Choose Your Color</label>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                {COLORS.map(color => {
                  const isTaken = takenColors.includes(color);
                  const isSelected = selectedColor === color;
                  return (
                    <button
                      key={color}
                      onClick={() => !isTaken && setSelectedColor(color)}
                      disabled={isTaken}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: COLOR_HEX[color],
                        border: isSelected ? '4px solid #333' : '4px solid transparent',
                        cursor: isTaken ? 'not-allowed' : 'pointer',
                        opacity: isTaken ? 0.3 : (isSelected ? 1 : 0.6),
                        transition: 'all 0.2s',
                        boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.3)' : 'none',
                      }}
                      title={isTaken ? `${color} is taken` : color}
                    />
                  );
                })}
              </div>
              {selectedColor && (
                <p style={{ textAlign: 'center', marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  Selected: <strong style={{ color: COLOR_HEX[selectedColor], textTransform: 'capitalize' }}>{selectedColor}</strong>
                </p>
              )}
            </div>
          )}

          <button
            onClick={createGame}
            disabled={gameMode === 'computer' && !selectedColor}
            style={{
              width: '100%',
              padding: '14px 22px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: "'Fredoka', sans-serif",
              cursor: gameMode === 'computer' && !selectedColor ? 'not-allowed' : 'pointer',
              background: gameMode === 'computer' && !selectedColor
                ? '#ccc'
                : 'linear-gradient(135deg, #6c7cff 0%, #6a4bb3 100%)',
              color: 'white',
              boxShadow: gameMode === 'computer' && !selectedColor ? 'none' : '0 10px 25px rgba(102, 126, 234, 0.35)',
              marginBottom: '18px',
              opacity: gameMode === 'computer' && !selectedColor ? 0.8 : 1,
            }}
          >
            {gameMode === 'computer' ? 'PLAY' : 'PLAY'}
          </button>

          {/* Join Game: non-live always; live only when multiplayer enabled */}
          {(!isLiveMode || liveThemeFlags.multiplayer) && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                margin: '24px 0',
                color: '#999',
                fontWeight: '600'
              }}>
                <div style={{ flex: 1, height: '2px', background: '#e0e0e0' }}></div>
                <span style={{ padding: '0 15px' }}>OR</span>
                <div style={{ flex: 1, height: '2px', background: '#e0e0e0' }}></div>
              </div>

              <div style={{ marginBottom: '22px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '8px',
                  textAlign: 'left'
                }}>Game ID</label>
                <input
                  type="text"
                  placeholder="Enter game ID"
                  value={joinGameId}
                  onChange={(e) => setJoinGameId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && joinGame()}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e7e7e7',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontFamily: "'Fredoka', sans-serif",
                    outline: 'none'
                  }}
                />
              </div>

              <button onClick={joinGame} style={{
                width: '100%',
                padding: '14px 22px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: "'Fredoka', sans-serif",
                cursor: 'pointer',
                background: '#f1f1f1',
                color: '#444'
              }}>
                JOIN GAME
              </button>
            </>
          )}
        </div>
      </div>
      {renderGamePopupPortal()}
    </>
    );
  }

  if (!gameState) {
    return (
      <>
      <div style={{
        fontFamily: "'Fredoka', sans-serif",
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px' }}>
          <p>Loading game...</p>
        </div>
      </div>
      {renderGamePopupPortal()}
    </>
    );
  }

  const renderBoard = () => {
    const cells = [];
    const cellSize = boardSize / 15;
    const safeCells = new Set([
      "1,6", "8,1", "13,8", "6,13",  // starting squares
      "6,2", "2,8", "8,12", "12,6"   // star/safe squares
    ]);

    for (let row = 0; row < 15; row++) {
      for (let col = 0; col < 15; col++) {
        let bg = 'white';
        let border = '1px solid #ddd';

        if (row < 6 && col < 6) {
          bg = '#51cf66';
          border = '1px solid #2f9e44';
        } else if (row < 6 && col >= 9) {
          bg = '#ffd43b';
          border = '1px solid #f59f00';
        } else if (row >= 9 && col >= 9) {
          bg = '#4dabf7';
          border = '1px solid #1971c2';
        } else if (row >= 9 && col < 6) {
          bg = '#ff6b6b';
          border = '1px solid #c92a2a';
        }
        else if (row === 7 && col >= 1 && col <= 5) {
          bg = '#c3fae8';
        } else if (col === 7 && row >= 1 && row <= 5) {
          bg = '#fff3bf';
        } else if (row === 7 && col >= 9 && col <= 13) {
          bg = '#d0ebff';
        } else if (col === 7 && row >= 9 && row <= 13) {
          bg = '#ffe3e3';
        }
        else if (row >= 6 && row <= 8 && col >= 6 && col <= 8) {
          // Center cells rendered by dedicated overlay below
          bg = 'transparent';
          border = 'none';
        }

        const isSafeCell = safeCells.has(`${col},${row}`);

        cells.push(
          <div
            key={`${row}-${col}`}
            style={{
              position: 'absolute',
              left: `${col * cellSize}px`,
              top: `${row * cellSize}px`,
              width: `${cellSize}px`,
              height: `${cellSize}px`,
              background: bg,
              border: border,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isSafeCell && (
              <span style={{
                color: '#ffd700',
                fontSize: `${Math.max(12, Math.floor(boardSize / 31))}px`,
                textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                userSelect: 'none'
              }}>
                ★
              </span>
            )}
          </div>
        );
      }
    }

    // Center area overlay — 4 colored triangles meeting at center point
    const centerSize = cellSize * 3;
    const centerBorderW = Math.max(2, Math.floor(cellSize * 0.07));
    const diagLineW = Math.max(1, Math.floor(cellSize * 0.05));

    cells.push(
      <div
        key="center-area"
        style={{
          position: 'absolute',
          left: `${6 * cellSize}px`,
          top: `${6 * cellSize}px`,
          width: `${centerSize}px`,
          height: `${centerSize}px`,
          aspectRatio: '1 / 1',
          zIndex: 3,
          overflow: 'hidden',
          border: `${centerBorderW}px solid #333`,
          boxSizing: 'border-box',
        }}
      >
        {/* Green triangle — LEFT (points right toward center) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: '#51cf66',
          clipPath: 'polygon(0% 0%, 50% 50%, 0% 100%)',
        }} />
        {/* Yellow triangle — TOP (points down toward center) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: '#ffd43b',
          clipPath: 'polygon(0% 0%, 100% 0%, 50% 50%)',
        }} />
        {/* Blue triangle — RIGHT (points left toward center) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: '#4dabf7',
          clipPath: 'polygon(100% 0%, 100% 100%, 50% 50%)',
        }} />
        {/* Red triangle — BOTTOM (points up toward center) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: '#ff6b6b',
          clipPath: 'polygon(0% 100%, 50% 50%, 100% 100%)',
        }} />
        {/* Diagonal dividing line — top-left to bottom-right */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '141.42%',
          height: `${diagLineW}px`,
          background: '#555',
          transform: 'translate(-50%, -50%) rotate(45deg)',
          pointerEvents: 'none',
        }} />
        {/* Diagonal dividing line — top-right to bottom-left */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '141.42%',
          height: `${diagLineW}px`,
          background: '#555',
          transform: 'translate(-50%, -50%) rotate(-45deg)',
          pointerEvents: 'none',
        }} />
      </div>
    );

    // Home area overlays — proper centered structure with 2×2 token slot grid
    const homeAreaConfigs = [
      { key: 'green', startRow: 0, startCol: 0, bg: '#51cf66', borderColor: '#2f9e44', tokenColor: '#2f9e44' },
      { key: 'yellow', startRow: 0, startCol: 9, bg: '#ffd43b', borderColor: '#f59f00', tokenColor: '#f59f00' },
      { key: 'blue', startRow: 9, startCol: 9, bg: '#4dabf7', borderColor: '#1971c2', tokenColor: '#1971c2' },
      { key: 'red', startRow: 9, startCol: 0, bg: '#ff6b6b', borderColor: '#c92a2a', tokenColor: '#c92a2a' },
    ];

    const homeAreaSize = cellSize * 6;
    const innerBoxSize = cellSize * 4;
    const homeBorderWidth = Math.max(2, Math.floor(cellSize * 0.08));
    const innerBoxRadius = Math.max(8, Math.floor(cellSize * 0.35));

    homeAreaConfigs.forEach(({ key: homeKey, startRow, startCol, bg: homeBg, borderColor: homeBorderColor, tokenColor }) => {
      cells.push(
        <div
          key={`home-${homeKey}`}
          style={{
            position: 'absolute',
            left: `${startCol * cellSize}px`,
            top: `${startRow * cellSize}px`,
            width: `${homeAreaSize}px`,
            height: `${homeAreaSize}px`,
            background: homeBg,
            border: `${homeBorderWidth}px solid ${homeBorderColor}`,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <div
            style={{
              width: `${innerBoxSize}px`,
              aspectRatio: '1 / 1',
              background: 'white',
              borderRadius: `${innerBoxRadius}px`,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              boxSizing: 'border-box',
            }}
          >
            {[0, 1, 2, 3].map((slotIdx) => (
              <div
                key={slotIdx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  aspectRatio: '1 / 1',
                }}
              >
                <div
                  style={{
                    width: '50%',
                    aspectRatio: '1 / 1',
                    borderRadius: '50%',
                    background: tokenColor,
                    opacity: 0.35,
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.15)',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    });

    return cells;
  };

  const diceValue = gameState?.diceValue;
  const isMyTurn = gameState?.currentTurn === playerColor;
  const myColor = COLOR_HEX[playerColor] || '#667eea';
  const turnColor = COLOR_HEX[gameState?.currentTurn] || myColor;
  const canRoll = isMyTurn && !diceValue && gameState?.started && !gameState?.players?.[playerColor]?.disabled;

  const showLiveCasualLobbyExtras =
    isLiveMode &&
    gameState?.gameMode === 'multiplayer' &&
    !urlConfig.tournamentId &&
    !gameState?.started &&
    Boolean(gameId);

  return (
    <>
    <div
      className="game-root"
      style={{
        fontFamily: "'Fredoka', sans-serif",
        minHeight: '100vh',
        padding: '20px',
        overflowX: 'hidden'
      }}
    >
      <div
        className="game-card"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '30px',
          padding: '30px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}
      >
        <div
          className="game-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '20px',
            borderBottom: '3px solid #f0f0f0'
          }}
        >
         {/* <h2 style={{
            margin: 0,
            color: '#667eea',
            fontFamily: "'Bubblegum Sans', cursive",
            fontSize: '36px'
          }}>LUDO</h2>  */}
          <div className="game-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div className="game-id" style={{
              fontSize: '14px',
              color: '#666',
              background: '#f8f8f8',
              padding: '10px 20px',
              borderRadius: '10px',
              fontWeight: '600'
            }}>
              <strong style={{ color: '#667eea', fontSize: '16px' }}>Game ID:</strong> {gameId}
            </div>
            <button onClick={leaveGame} className="leave-btn" style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '10px',
              background: '#ff6b6b',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              Leave Game
            </button>
          </div>
        </div>

        {/* Waiting lobby: joined players only; timer only when tournament and under 2 players */}
        {!gameState.started && (
          <div
            className="ludo-waiting-lobby"
            style={{
              width: '100%',
              maxWidth: '100%',
              marginBottom: '28px',
              textAlign: 'center',
            }}
          >
            <h2
              style={{
                margin: '0 0 12px',
                fontSize: 'clamp(20px, 4vw, 26px)',
                fontWeight: 800,
                color: '#111827',
                letterSpacing: '-0.02em',
              }}
            >
              Waiting for users…
            </h2>
            {urlConfig.tournamentId && (
              <p
                style={{
                  margin: '0 auto 14px',
                  maxWidth: '420px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#64748b',
                  lineHeight: 1.4,
                }}
              >
                Match starts when 2 players join. Others can still join after the game begins.
              </p>
            )}
            {showLiveCasualLobbyExtras && (
              <div
                style={{
                  margin: '0 auto 16px',
                  maxWidth: '420px',
                  padding: '12px 16px',
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  color: '#1e293b',
                  background: '#f1f5f9',
                  borderRadius: '12px',
                  border: '1px solid #cbd5e1',
                  wordBreak: 'break-all',
                }}
              >
                <span
                  style={{
                    color: '#64748b',
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '13px',
                  }}
                >
                  Share this Game ID
                </span>
                <code
                  style={{
                    fontFamily: 'ui-monospace, Menlo, monospace',
                    fontSize: '1.05em',
                    fontWeight: 800,
                    color: '#0f172a',
                  }}
                >
                  {gameId}
                </code>
              </div>
            )}
            {urlConfig.tournamentId &&
              gameState.players &&
              Object.keys(gameState.players).length < 2 && (
                <div
                  style={{
                    marginBottom: '18px',
                    fontSize: '17px',
                    fontWeight: 600,
                    color: '#374151',
                  }}
                >
                  Time remaining:{' '}
                  {String(Math.floor(lobbyTimerSeconds / 60)).padStart(2, '0')}:
                  {String(lobbyTimerSeconds % 60).padStart(2, '0')}
                </div>
              )}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: `${LOBBY_JOINED_CARD_GAP_PX}px`,
                width: '100%',
              }}
            >
              {COLORS.filter((c) => gameState.players?.[c]).map((color) => {
                const player = gameState.players[color];
                const seatNum = COLORS.indexOf(color) + 1;
                const subtitle =
                  player.company || player.orgName || player.organization;
                const lobbyCardTheme =
                  LOBBY_CARD_THEME[color] ?? LOBBY_CARD_DEFAULT_THEME;
                return (
                  <div
                    key={color}
                    style={{
                      position: 'relative',
                      boxSizing: 'border-box',
                      flexShrink: 0,
                      width: `min(${LOBBY_JOINED_CARD_WIDTH_PX}px, calc((100% - ${LOBBY_JOINED_CARD_GAP_PX}px) / 2))`,
                      minHeight: '168px',
                      padding: '20px 16px 18px',
                      borderRadius: '15px',
                      textAlign: 'center',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      background: lobbyCardTheme.gradient,
                      border: `2px solid ${lobbyCardTheme.border}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '12px',
                    }}
                  >
                    {!showLiveCasualLobbyExtras && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: lobbyCardTheme.badgeBg,
                          border: `2px solid ${lobbyCardTheme.badgeBorder}`,
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 800,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        {seatNum}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: '6px',
                        padding: '4px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: lobbyCardTheme.avatarBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                            fill={
                              lobbyCardTheme.avatarBg === COLOR_HEX.yellow
                                ? '#1c1917'
                                : '#ffffff'
                            }
                          />
                        </svg>
                      </div>
                    </div>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: '16px',
                        lineHeight: 1.25,
                        wordBreak: 'break-word',
                        width: '100%',
                        color: '#111827',
                      }}
                    >
                      {player.name}
                    </div>
                    {subtitle ? (
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          lineHeight: 1.35,
                          wordBreak: 'break-word',
                          width: '100%',
                          color: '#374151',
                        }}
                      >
                        {subtitle}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!gameState.started &&
          gameState.gameMode !== 'computer' &&
          !(
            urlConfig.tournamentId &&
            gameState.players &&
            Object.keys(gameState.players).length < 2
          ) && (
            <button
              className="ludo-start-game-btn"
              onClick={startGame}
              style={{
                padding: '18px',
                border: 'none',
                borderRadius: '15px',
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                marginBottom: '20px',
              }}
            >
              Start Game
            </button>
          )}

        {showGameStartedIndicator && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              padding: '16px 28px',
              background: 'rgba(0,0,0,0.85)',
              color: '#fff',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: '600',
              fontFamily: "'Fredoka', sans-serif",
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              pointerEvents: 'none',
            }}
          >
            Game started!
          </div>
        )}

        {showYourTurnIndicator && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              padding: '16px 28px',
              background: 'rgba(0,0,0,0.85)',
              color: myColor,
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: '600',
              fontFamily: "'Fredoka', sans-serif",
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              pointerEvents: 'none',
            }}
          >
            Your turn!
          </div>
        )}

        {gameState.started && (
          <div className="game-main" style={{ display: 'flex', gap: '85px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Board wrapper: panels on left/right (desktop), top/bottom (mobile) */}
            <div className="board-wrapper" style={{ position: 'relative', margin: '0 140px' }}>
              {(() => {
                const gradients = {
                  green: 'linear-gradient(135deg, #51cf66, #37b24d)',
                  yellow: 'linear-gradient(135deg, #ffd43b, #fab005)',
                  blue: 'linear-gradient(135deg, #4dabf7, #339af0)',
                  red: 'linear-gradient(135deg, #ff6b6b, #ee5a6f)',
                };
                const pieceColors = { green: '#2f9e44', yellow: '#f59f00', blue: '#1971c2', red: '#c92a2a' };
                const panelPositions = {
                  green:  { left: 0, top: 0, transform: 'translateX(calc(-100% - 12px))' },
                  yellow: { right: 0, top: 0, transform: 'translateX(calc(100% + 12px))' },
                  red:    { left: 0, bottom: 0, transform: 'translateX(calc(-100% - 12px))' },
                  blue:   { right: 0, bottom: 0, transform: 'translateX(calc(100% + 12px))' },
                };
                const players = gameState?.players || {};
                return Object.entries(players).map(([color, player]) => {
                  const isTurn = gameState.currentTurn === color;
                  const pos = panelPositions[color] || panelPositions.green;
                  return (
                    <div
                      key={`panel-${color}`}
                      className={`player-panel player-panel-${color}`}
                      style={{
                        position: 'absolute',
                        ...pos,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 16px',
                        borderRadius: '12px',
                        background: gradients[color],
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        border: isTurn ? '3px solid #111' : '3px solid transparent',
                        transition: 'border 0.3s',
                        whiteSpace: 'nowrap',
                        zIndex: 5,
                      }}
                    >
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        background: pieceColors[color], border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)', flexShrink: 0,
                      }} />
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <div style={{ fontWeight: '700', fontSize: '15px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{player.name}</div>
                        {gameState.gameMode === 'multiplayer' && (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {[0, 1, 2].map((i) => {
                              const missed = player.missedTurns ?? 0;
                              const isRed = i < missed;
                              return (
                                <div
                                  key={i}
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: isRed ? '#ff6b6b' : '#51cf66',
                                    flexShrink: 0,
                                  }}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}

              <div
                className="board-container"
                style={{
                  position: 'relative',
                  width: `${boardSize}px`,
                  height: `${boardSize}px`,
                  border: `${Math.max(4, Math.floor(boardSize / 70))}px solid #333`,
                  borderRadius: `${Math.max(6, Math.floor(boardSize / 56))}px`,
                  overflow: 'visible',
                  background: '#f0f0f0',
                  transform: 'none',
                  maxWidth: '100%',
                  margin: '0 auto',
                  opacity: (gameState.winner || gameState.abandoned) ? 0.4 : 1,
                  filter: (gameState.winner || gameState.abandoned) ? 'grayscale(0.3)' : 'none',
                  pointerEvents: (gameState.winner || gameState.abandoned) ? 'none' : 'auto',
                  transition: 'opacity 0.3s ease, filter 0.3s ease'
                }}
              >
                {renderBoard()}

                {(() => {
                  // Collect all pieces with their positions
                  const allPieces = [];
                  if (gameState && gameState.players) {
                    Object.entries(gameState.players).forEach(([color, player]) => {
                      if (!player || !Array.isArray(player.pieces)) return;
                      player.pieces.forEach((_, pieceIndex) => {
                        const pos = getPiecePosition(color, pieceIndex);
                        allPieces.push({ color, pieceIndex, pos });
                      });
                    });
                  }
                  
                  // Group pieces by cell position (round to detect same cell)
                  const cellSize = boardSize / 15;
                  const posKey = (pos) => `${Math.round(pos.x / cellSize)}_${Math.round(pos.y / cellSize)}`;
                  const groups = {};
                  allPieces.forEach(p => {
                    const key = posKey(p.pos);
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(p);
                  });
                  
                  // Location-pin token colors per player
                  const PIN_COLORS = {
                    green:  { main: '#2b9a3e', light: '#5cb85c', lighter: '#a8e6b4', dark: '#1a6b28', rim: '#0f4a1c' },
                    yellow: { main: '#d48000', light: '#f5b800', lighter: '#ffe9a0', dark: '#9a5c00', rim: '#6b3d00' },
                    blue:   { main: '#1565c0', light: '#4a90d9', lighter: '#b3d4f5', dark: '#0d47a1', rim: '#082f6b' },
                    red:    { main: '#b71c1c', light: '#e53935', lighter: '#ffcdd2', dark: '#8b0000', rim: '#5c0000' },
                  };

                  // ~33% bigger: boardSize/20 → boardSize/15
                  const pinW = Math.max(22, Math.floor(boardSize / 15));
                  const pinH = Math.round(pinW * (34 / 24));
                  const offsetAmount = pinW * 0.50;

                  // Horizontal-only stacking so pins never push below cell
                  const stackOffsets = {
                    1: [{ x: 0 }],
                    2: [{ x: -0.30 }, { x: 0.30 }],
                    3: [{ x: -0.30 }, { x: 0.30 }, { x: 0 }],
                    4: [{ x: -0.28 }, { x: 0.28 }, { x: -0.10 }, { x: 0.10 }],
                  };

                  // How far pin bottom exceeds cell bottom if centered with -50%
                  const bottomOverflow = Math.max(0, pinH / 2 - cellSize / 2);

                  return allPieces.map(({ color, pieceIndex, pos }) => {
                    const key = posKey(pos);
                    const group = groups[key];
                    const groupCount = Math.min(group.length, 4);
                    const indexInGroup = group.findIndex(p => p.color === color && p.pieceIndex === pieceIndex);
                    const sOff = (stackOffsets[groupCount] || stackOffsets[4])[indexInGroup % groupCount];

                    const myTurnWithDice =
                      gameState.currentTurn === playerColor &&
                      gameState.diceValue &&
                      gameState.lastRoll;
                    const canMove =
                      myTurnWithDice &&
                      movablePieces.includes(pieceIndex) &&
                      color === playerColor &&
                      !isMoving;
                    const c  = PIN_COLORS[color] || PIN_COLORS.red;
                    const id = `${color}-${pieceIndex}`;

                    return (
                      <div
                        key={id}
                        onClick={() => canMove && !isMoving && movePiece(pieceIndex)}
                        style={{
                          position: 'absolute',
                          width:  `${pinW}px`,
                          height: `${pinH}px`,
                          left: `${pos.x + sOff.x * offsetAmount}px`,
                          top:  `${pos.y - bottomOverflow}px`,
                          transform: 'translate(-50%, -50%)',
                          cursor:    canMove ? 'pointer' : 'default',
                          zIndex:    canMove ? 100 : 10 + indexInGroup,
                          transition: 'left 0.4s ease-out, top 0.4s ease-out',
                        }}
                      >
                        <div style={{
                          width: '100%', height: '100%',
                          animation: canMove ? 'pinBounce 0.65s ease-in-out infinite' : 'none',
                        }}>
                        <svg
                          width={pinW} height={pinH}
                          viewBox="0 0 24 34"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ display: 'block', overflow: 'visible' }}
                        >
                          <defs>
                            <radialGradient id={`pin-body-${id}`} cx="38%" cy="30%" r="70%">
                              <stop offset="0%"   stopColor={c.lighter} />
                              <stop offset="28%"  stopColor={c.light}   />
                              <stop offset="65%"  stopColor={c.main}    />
                              <stop offset="100%" stopColor={c.dark}    />
                            </radialGradient>
                            <radialGradient id={`pin-inner-${id}`} cx="38%" cy="38%" r="62%">
                              <stop offset="0%"   stopColor={c.main} />
                              <stop offset="100%" stopColor={c.rim}  />
                            </radialGradient>
                            <radialGradient id={`pin-gloss-${id}`} cx="28%" cy="22%" r="44%">
                              <stop offset="0%"   stopColor="rgba(255,255,255,0.82)" />
                              <stop offset="55%"  stopColor="rgba(255,255,255,0.18)" />
                              <stop offset="100%" stopColor="rgba(255,255,255,0)"    />
                            </radialGradient>
                            <clipPath id={`pin-clip-${id}`}>
                              <path d="M12,1 C20.5,1 23,6.5 23,12 C23,20.5 12,33 12,33 C12,33 1,20.5 1,12 C1,6.5 3.5,1 12,1 Z" />
                            </clipPath>
                          </defs>

                          {/* Ground shadow */}
                          <ellipse cx="12" cy="33.2" rx="4.5" ry="1.1" fill="rgba(0,0,0,0.22)" />

                          {/* Outer pin shell */}
                          <path
                            d="M12,1 C20.5,1 23,6.5 23,12 C23,20.5 12,33 12,33 C12,33 1,20.5 1,12 C1,6.5 3.5,1 12,1 Z"
                            fill={`url(#pin-body-${id})`}
                          />

                          {/* White ring inside the dome */}
                          <circle cx="12" cy="11.5" r="7.8" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.4" />

                          {/* Inner dark circle */}
                          <circle cx="12" cy="11.5" r="5.8" fill={`url(#pin-inner-${id})`} />

                          {/* Shine dot */}
                          <ellipse cx="10.2" cy="9.8" rx="1.7" ry="1.3" fill="rgba(255,255,255,0.82)" />

                          {/* Gloss overlay */}
                          <rect x="0" y="0" width="24" height="33" fill={`url(#pin-gloss-${id})`} clipPath={`url(#pin-clip-${id})`} />

                          {/* Specular arc */}
                          <path d="M 7.5,6.5 C 9,4.8 11.8,4.2 14,5.2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" fill="none" strokeLinecap="round" />

                          {/* Gold ring when movable */}
                          {canMove && (
                            <circle cx="12" cy="11.5" r="11.2" fill="none" stroke="gold" strokeWidth="1.8" strokeDasharray="5,3" strokeLinecap="round" opacity="0.95">
                              <animateTransform attributeName="transform" type="rotate" from="0 12 11.5" to="360 12 11.5" dur="2s" repeatCount="indefinite" />
                            </circle>
                          )}
                        </svg>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="dice-panel" style={{ textAlign: 'center' }}>
              <div
                className="dice-box"
                onClick={rollDice}
                style={{
                  width: '120px',
                  height: '120px',
                  background: 'white',
                  border: `5px solid ${turnColor}`,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '64px',
                  fontWeight: 'bold',
                  color: turnColor,
                  cursor: canRoll && !isDiceRolling ? 'pointer' : 'not-allowed',
                  opacity: canRoll && !isDiceRolling ? 1 : 0.5,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  margin: '0 auto 20px',
                  animation: isDiceRolling ? 'diceRoll 0.15s ease-in-out infinite' : 'none',
                  flexShrink: 0,
                }}
              >
                {isDiceRolling
                  ? (diceDisplayValue ? DICE_FACES[diceDisplayValue - 1] : '…')
                  : (diceValue ? DICE_FACES[diceValue - 1] : '🎲')}
              </div>
              <div className="dice-info">
                <div className="dice-result-text" style={{ fontSize: '16px', color: myColor, fontWeight: '600', marginBottom: '8px', visibility: (isMyTurn && diceValue) ? 'visible' : 'hidden' }}>
                  You got {diceValue || ' '}
                </div>
                <div className="dice-status-text" style={{
                  fontSize: '18px',
                  color: '#666',
                  fontWeight: '600',
                  maxWidth: '200px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '10px'
                }}>
                  {isMyTurn
                    ? diceValue
                      ? movablePieces.length > 0
                        ? 'Click a token to move!'
                        : 'No moves available...'
                      : 'Your turn! Roll the dice'
                    : `${gameState.players[gameState.currentTurn]?.name}'s turn`}
                </div>
                {gameState.started && (
                  <div className="turn-timer" style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: turnTimer <= 10 ? '#ff6b6b' : "black",
                    marginTop: '12px',
                    padding: '10px 20px',
                    background: 'white',
                    borderRadius: '12px',
                    border: `3px solid ${turnTimer <= 10 ? '#ff6b6b' : "black"}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: '80px',
                    textAlign: 'center'
                  }}>
                    {turnTimer}s
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-10px); }
        }
        @keyframes pinBounce {
          0%, 100% { transform: translateY(0); }
          45%      { transform: translateY(-7px); }
        }
        @keyframes diceRoll {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.15) rotate(-8deg); }
          50% { transform: scale(1.1) rotate(8deg); }
          75% { transform: scale(1.15) rotate(-5deg); }
        }
        .landing-card {
          width: min(420px, 92vw);
        }
        /* Keep dice panel width stable on desktop to avoid layout "shaking" */
        @media (min-width: 769px) {
          .dice-panel {
            width: 250px;
            min-width: 250px;
            max-width: 250px;
            flex: 0 0 250px;
          }
        }
        @media (max-width: 900px) {}
        @media (max-width: 640px) {
          .landing-root {
            padding: 14px !important;
          }
          .landing-card {
            padding: 28px !important;
          }

        }
        @media (max-width: 480px) {
          .landing-card {
            padding: 22px !important;
            border-radius: 18px !important;
          }

        }
        @media (min-width: 901px) {
          .game-card {
              margin-top:50px !important;
          }
          body{
           overflow:hidden;
          }
        }
        @media (max-width: 768px) {
          .game-root {
            padding: 16px !important;
          }
          .game-card {
            padding: 16px !important;
            border-radius: 22px !important;
          }
          .game-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px;
            text-align: center;
          }
          .game-actions {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }
          .game-id {
            text-align: center;
            padding: 8px 12px !important;
            word-break: break-all;
          }
          .leave-btn {
            width: 100%;
          }
          .players-grid {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
          .board-wrapper {
            margin: 0 auto !important;
            display: grid !important;
            grid-template-columns: 1fr 1fr;
            width: min(560px, 92vw) !important;
            max-width: 100% !important;
            gap: 6px;
          }
          .player-panel {
            position: static !important;
            transform: none !important;
            width: auto !important;
            flex: none !important;
            box-sizing: border-box;
            justify-content: center;
            padding: 3px 6px !important;
            gap: 4px !important;
            font-size: 9px !important;
            border-radius: 6px !important;
            min-height: 0 !important;
            border-width: 2px !important;
          }
          .player-panel > div:first-child {
            width: 18px !important;
            height: 18px !important;
            border-width: 2px !important;
          }
          .player-panel > div:last-child > div {
            font-size: 10px !important;
            max-width: 70px !important;
          }
          .player-panel-green {
            grid-column: 1;
            grid-row: 1;
          }
          .player-panel-yellow {
            grid-column: 2;
            grid-row: 1;
          }
          .player-panel-red {
            grid-column: 1;
            grid-row: 3;
          }
          .player-panel-blue {
            grid-column: 2;
            grid-row: 3;
          }
          .board-wrapper .board-container {
            grid-column: 1 / -1;
            grid-row: 2;
            margin: 0 auto !important;
          }
          .game-main {
            flex-direction: column !important;
            align-items: center !important;
            gap: 18px !important;
            overflow: visible !important;
          }
          .dice-panel {
            width: 100%;
            text-align: left;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 16px;
            flex-wrap: nowrap;
            height:95px;
          }
          .dice-panel .dice-box {
            width: 80px !important;
            height: 80px !important;
            font-size: 44px !important;
            margin: 0 !important;
            flex-shrink: 0;
          }
          .dice-panel .dice-info {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
            min-width: 0;
          }
          .dice-result-text {
            font-size: 11px !important;
            margin-bottom: 4px !important;
          }
          .dice-status-text {
            font-size: 12px !important;
            padding: 8px 10px !important;
            max-width: 150px !important;
          }
          .turn-timer {
            font-size: 12px !important;
            padding: 5px 10px !important;
            margin-top: 6px !important;
            min-width: 40px !important;
            border-width: 2px !important;
          }

        }
        @media (max-width: 480px) {
          .game-root {
            padding: 8px !important;
          }
          .game-card {
            padding: 10px !important;
          }

        }
        /* Avoid affecting small screens (mobile) */
        @media (min-width: 769px) and (max-height: 740px) {
          .dice-panel {
            width: 250px;
          }
        }
        @media (max-width: 900px) and (orientation: landscape) {
          .game-card {
            padding: 14px !important;
          }
          .game-main {
            flex-direction: row;
            align-items: flex-start;
          }
          
          .dice-panel {
            max-width: 220px;
          }
        }
      `}</style>
    </div>
    {renderGamePopupPortal()}
    </>
  );
};

export default LudoGame;